import { Vue, Component, Prop, Watch, Emit} from 'vue-property-decorator';
import Modal from '@/assets/common/Modal/Modal.vue'
import store from '@/assets/common/store/store';
import { CollectionAndShippingGroup, ShippingItemDetail, AgriField, WarehouseChemicalWithWPU, WarehouseFertilizerWithWPU } from '@/types/Component';
import FertilizerSelectionModal from '@/assets/common/Modal/FertilizerSelectionModal/FertilizerSelectionModal.vue'
import FertilizerUsageModal from '@/assets/common/Modal/FertilizerUsageModal/FertilizerUsageModal.vue';
import SelectPlantingsAndAgriFields from '@/components/Record/SelectPlantingsAndAgriFields/SelectPlantingsAndAgriFields.vue'
import { SelectedPlantingAgriFieldPair } from '@/components/Record/SelectPlantingsAndAgriFields/SelectPlantingsAndAgriFields'
import FertilizerModal from '@/assets/common/Modal/FertilizerModal/FertilizerModal.vue';
import PesticideSelectionModal from '@/assets/common/Modal/PesticideSelectionModal/PesticideSelectionModal.vue';
import PesticideUsageModal from '@/assets/common/Modal/PesticideUsageModal/PesticideUsageModal.vue';
import { toCamel } from 'snake-camel';
import { WorkItem } from '@/types';
import { AgriFieldOnPlantingResponse } from '@/types/APIResponse';
import SeedlingModal from '@/assets/common/Modal/SeedlingModal/SeedlingModal.vue';

type SeedlingUnit = {
    id: string;
    unit: string;
};

type Fertilizer = {
    id: string;
    pdFertilizerId?: string | null;
    name: string;
    maker: string;
    chemRetio: number | null;
    unitName: string;
    warehouseId : string;
    warehouseName : string;
    ownerClass: number;
}

type Chemical = {
    id: number;
    name: string;
    unitName: string;
    warehouseId : string;
    warehouseName : string;
}

type ProcessType = 'ADD' | 'UPDATE' | 'NOCHANGE';

type PairShare = {
    plantingId: string | null;
    tempPlantingKey?: string | null;
    agriFieldId: string;
    usageQty: number | null;
    amount: number | null;
    workRecordId?: string | null;
    workRecordUpdated?: string | null;
    originalUsageQty?: number | null;
    originalUsageUnit?: string | null;
    originalUnitOfMeasure?: number | null;
};

type ChemicalPairShare = {
    plantingId: string | null;
    tempPlantingKey?: string | null;
    agriFieldId: string;
    dilRatio: number | null;
    sprayQty: number | null;
    usageQty: number | null;
    amount: number | null;
    workRecordId?: string | null;
    workRecordUpdated?: string | null;
    originalUsageQty?: number | null;
    originalUsageUnit?: string | null;
    originalUnitOfMeasure?: number | null;
};

type ShareLock = { workRecordId?: string | null; workRecordUpdated?: string | null };

type WorkUsage = {
    localId: string | null;
    id: string | null;
    workRecordId: string | null;
    workDate: Date | string | null;
    usageQty: number | null;
    usageUnit: string | null;
    unitOfMeasure: number;
    amount: number | null;
    updated: string | null;
    transactions: Transaction[];
    processType: ProcessType;
    editable?: boolean;
    shares?: PairShare[] | null;
    fertLinkId?: string | null;
};

type CvhFertilizer = {
    fertilizerId: string;
    resolvedFertilizerId?: string;
    fertilizerName: string;
    maker: string | null;
    chemRetio: number | null;
    warehouseId: string;
    warehouseName: string;
    workUsages: WorkUsage[];
    ownerClass: number;
};

type CvhChemical = {
    chemicalId: number;
    chemicalName: string;
    warehouseId: string;
    disiDiv: number | null;
    warehouseName: string;
    memo: string | null;
    usageCountOrigin: string | null;
    usageCount: number | null;
    ingredients: Ingredient[];
    cropId: string | null;
    cropName: string | null;
    appPartBranchNum: number | null;
    usageCountBranchNum: number | null;
    dilRatioBranchNum: number | null;
    sprayQtyBranchNum: number | null;
    dilRatioOrigin: string | null;
    sprayQtyOrigin: string | null;
    al: string | null;
    appPestWeed: string | null;
    purposeUse: string | null;
    whenUse: string | null;
    howUse: string | null;
    fumTime: string | null;
    fumTemp: string | null;
    applSoil: string | null;
    applArea: string | null;
    applChem: string | null;
    workUsages: ChemicalWorkUsage[];
    isExpired: boolean;
    isNotSuitable: boolean;
    hasCaps: boolean;
    dilRatioMin: number | null;
    dilRatioMax: number | null;
    sprayQtyMin: number | null;
    sprayQtyMax: number | null;
    conditionsNotSelected: boolean;
    cropOutOfScope: boolean;
    famicUpdated: string | null;
};

type Ingredient = {
    branchNum: number;
    name : string;
    isExpired: boolean;
    pointValue: number;
};

type ChemicalWorkUsage = {
    chemicalId: number;
    localId: string | null;
    id: string | null;
    workRecordId: string | null;
    workDate: Date | string | null;
    cropId: string | null;
    appPartBranchNum : number | null;
    usageCountBranchNum : number | null;
    dilRatioBranchNum : number | null;
    sprayQtyBranchNum : number | null;
    unitOfMeasure: number;
    dilRatio : number | null;
    dilRatioUnit : string | null;
    disiDiv: number;
    usageQty: number | null;
    usageUnit: string | null;
    sprayQty: number | null;
    sprayUnit: string | null;
    shares : number | null;
    ingSelConditions: SelCondition[];
    amount: number | null;
    updated: string | null;
    transactions: Transaction[];
    processType: ProcessType;
    editable?: boolean;
    pairShares?: ChemicalPairShare[] | null;
    dilRatioAtSplit?: number | null;
    sprayQtyAtSplit?: number | null;
    chemLinkId?: string | null;
};

type WorkRecord = {
    id: string;
    workDate: Date | string | null;
    workItemId: string | null;
    workItemName: string | null;
    status: string | '追加' | '編集' | null;
    editable: boolean;
    updated: string | null;
    processType: ProcessType;
    workLinkId: string | null;
};

type WorkRecordBatchMember = {
    plantingId: string | null;
    agriFieldId: string;
    workRecordId: string;
    updated: string | null;
};

type WorkRecordBatch = {
    id: string;
    workLinkId: string | null;
    workItemId: string | null;
    workItemName: string | null;
    workDate: Date | string | null;
    editable: boolean;
    hasProtectedResourceData: boolean;
    processType: ProcessType;
    members?: WorkRecordBatchMember[];
};

type ShippingItem = {
    id: string;
    name: string;
    isHidden: boolean; 
    comId: string;
};

type Seedling = {
    id: string;
    name: string;
    cropFamicId: string;
    memo: string;
    isHidden: boolean;
}

type Tenant = {
    id: string;
    tenantName: string;
    deleted: boolean;
};

type Producer = {
    pdId: string;
    pdName: string;
    pdNo: string;
    isHidden: boolean;
    delDate: string;
    tenants: Tenant[];
};

type Committee = {
    id: string;
    name: string;
    isHidden: boolean;
    producers: Producer[];
};

type CvHistory = {
    tenantId: string | null;
    comId: string | null;
    pdId: string | null;
    pdTenantId: string | null;
    sitemId: string | null;
    plantingId: string | null;
    plantingName: string | null;
    plantingStartDate?: Date | null;
    plantingEndDate?: Date | null;
    seedlingId: string | null;
    seedlingQty: number | null;
    seedlingUnitId: string | null;
    agriFieldId: string | null;
    agriFieldNum: string | null;
    agriFieldBranchNum: string | null;
    agriFieldUsageArea: number | null;
    totalAgriFieldUsageArea: number | null;
    remainingAgriFieldUsageArea?: number | null;
    prevCrops: string | null;
    cropType: string | null;
    purTrns: string | null,
    seedSd: number | null;
    checkPeriod: number | null;
    totalYield: number | null;
    totalShpVol: number | null;
    created: string | null;
    createdUser: string | null;
    updated: string | null;
    updatedUser: string | null;
    updatedLocal: string | null;
    createdUserName: string | null;
    updatedUserName: string | null;
};

type PairBasicData = {
    existingCvId: string | null;
    updatedLocal: string | null;
    tempPlantingKey: string | null;
    plantingId: string | null;
    plantingName: string | null;
    plantingStartDate: Date | null;
    plantingEndDate: Date | null;
    seedlingId: string | null;
    seedlingQty: number | null;
    seedlingUnitId: string | null;
    agriFieldId: string | null;
    agriFieldName: string | null;
    agriFieldNum: string | null;
    agriFieldBranchNum: string | null;
    agriFieldUsageArea: number | null;
    totalAgriFieldUsageArea: number | null;
    remainingAgriFieldUsageArea: number | null;
    prevCrops: string | null;
    cropFamicId: string | null;
    cropName: string | null;
    workRecords: WorkRecord[];
    fertilizers: CvhFertilizer[];
    chemicals: CvhChemical[];
    seedSd: number | null;
    checkPeriod: number | null;
    cropType: string | null;
    purTrns: string | null;
    totalYield: number | null;
    totalShpVol: number | null;
    created: string | null;
    createdUserName: string | null;
    updated: string | null;
    updatedUserName: string | null;
};

type FertilizerUsage = {
    id: string | null;
    fertilizerId: string;
    fertilizerName: string;
    chemRetio: number | null;
    warehouseId: string;
    warehouseName: string;
    workRecordId: string | null;
    workDate: Date | string | null;
    usageQty: number | null;
    usageUnit: string | null;
    unitOfMeasure: number;
    usedDates: Date[];
    editable: boolean;
    amount: number | null;
    updated: string | null;
    transactions?: Transaction[];
    processType?: ProcessType;
    shares?: PairShare[] | null;
};

type ChemicalUsage = {
    id: string | null;
    chemicalId: number | null;
    chemicalName: string | null;
    cropId: string | null;
    appPartBranchNum : number | null;
    usageCountBranchNum : number | null;
    dilRatioBranchNum : number | null;
    sprayQtyBranchNum : number | null;
    warehouseId: string | null;
    warehouseName: string | null;
    workRecordId: string | null;
    workDate: Date | string | null;
    usageQty: number | null;
    usageUnit: string | null;
    sprayQty: number | null;
    sprayUnit: string | null;
    dilRatio : number | null;
    dilRatioUnit : string | null;
    unitOfMeasure: number;
    disiDiv: number;
    shares : number | null;
    usedDates: Date[];
    editable: boolean;
    isNotSuitable: boolean;
    isExpired: boolean;
    ingSelConditions: SelCondition[];
    amount: number | null;
    hasCaps: boolean;
    updated: string | null;
    transactions?: Transaction[];
    processType?: ProcessType;
    famicUpdated?: string | null;
    cropOutOfScope?: boolean;
    pairShares?: ChemicalPairShare[] | null;
    dilRatioAtSplit?: number | null;
    sprayQtyAtSplit?: number | null;
};

type SelCondition = {
    chemicalId: number;
    isExpired: boolean;
    name: string;
    basicBranchNum: number;
    ingUsageCntBranchNum: number;
    usageCountOrigin: string | null;
    usageCount: number | null;
}

type Transaction = {
    id: string | null;
    updated: string | null;
}

type ChemConditionDetailIng = {
    branchNum: number;
    chemicalId: number;
};

type ChemConditionIngGroup = {
    branchNum: number;
    conditions: ChemConditionDetailIng[];
};

type ChemConditionAppPart = {
    branchNum: number;
    usageConditions: { branchNum: number }[];
    dilutionConditions: { branchNum: number }[];
    sprayConditions: { branchNum: number }[];
    ingConditions: ChemConditionIngGroup[];
};

type ChemConditionSettings = {
    caps: ChemConditionAppPart[];
};

@Component({
    components: {
        Modal,
        FertilizerSelectionModal,
        FertilizerUsageModal,
        SelectPlantingsAndAgriFields,
        FertilizerModal,
        PesticideSelectionModal,
        PesticideUsageModal,
        SeedlingModal
    }
})
export default class CultivationHistoryRegisterModal extends Vue {

    @Prop()
    showCvhModal!: boolean

    @Prop()
    mode?: string | 'new' | 'edit'

    @Prop()
    id?: string | null

    @Prop()
    groupId?: string | null

    @Prop()
    shippingItemId?: string | null

    @Prop()
    committeeId?: string | null

    @Prop()
    producerId?: string | null

    @Prop()
    tenantId?: string | null

    isLoading = false;
    isInitializing = false;
    private initSeq = 0;
    today = new Date().toISOString().split('T')[0];
    cropFamicId: string | null = null;
    cropName: string | null = null;
    warehouseChemicals = [] as WarehouseChemicalWithWPU[];
    warehouseFertilizers = [] as WarehouseFertilizerWithWPU[];
    chemicalUnits = [] as any[];

    get title(): string {
        return this.mode === 'new' ? '新規栽培履歴' : '栽培履歴情報';
    }

    get isCultivationHistoryModalOpen():boolean{
        return(
            this.showFertilizerSelectionModal ||
            this.showFertilizerUsageModal ||
            this.showPesticideUsageModal ||
            this.isShowPesticideSelectionModal ||
            this.showSelectPlantingsAndAgriFields ||
            this.showFertilizerModal ||
            this.showSeedlingModal 
        )
    }

    truncateText(text: string | null | undefined, maxLength: number): string {
        const safeText = text || '';
        if (safeText.length > maxLength) {
            return safeText.substring(0, maxLength) + '...';
        }
        return safeText;
    }

    baseUrl = '/cultivation_histories';
    collectionAndShippingGroups: CollectionAndShippingGroup[] = [];
    selectedCollectionAndShippingGroup: CollectionAndShippingGroup | null = null;
    committees: Committee[] = [];
    producers: Producer[] = [];
    tenants: Tenant[] = [];
    seedlings: Seedling[] = [];
    seedlingUnits: SeedlingUnit[] = [];
    shippingItems: ShippingItem[] = [];
    agriFields: AgriField[] = [];
    workItems: WorkItem[] = [];
    form: CvHistory = this.getEmptyForm();
    sitem: ShippingItemDetail | null = null;
    showFertilizerSelectionModal = false;
    showFertilizerUsageModal = false;
    showPesticideUsageModal = false;
    isShowPesticideSelectionModal = false;
    showSelectPlantingsAndAgriFields = false;
    selectedPairs: PairBasicData[] = [];
    activePairIndex = 0;
    editingOriginalPairKey: string | null = null;
    workTabPairSelection: 'bulk' | number = 'bulk';
    bulkWorkRecords: WorkRecordBatch[] = [];
    private originalBulkWorkRecords: WorkRecordBatch[] = [];
    deletedWorkRecordBatchIds: string[] = [];
    private deletedWorkRecordExpectedUpdates = new Map<string, string>();
    fertTabPairSelection: 'bulk' | number = 'bulk';
    bulkFertilizers: CvhFertilizer[] = [];
    private originalBulkFertilizers: CvhFertilizer[] = [];
    deletedFertilizerBatchIds: string[] = [];
    isEditingBulkFertilizer = false;
    chemTabPairSelection: 'bulk' | number = 'bulk';
    bulkChemicals: CvhChemical[] = [];
    private originalBulkChemicals: CvhChemical[] = [];
    deletedChemicalBatchIds: string[] = [];
    isEditingBulkChemical = false;
    detailBodyHeight: number | null = null;
    private pairHeightObserver: ResizeObserver | null = null;
    showFertilizerModal = false;
    isNewFertilizer = false;
    isNewSeedling = false;
    showSeedlingModal = false;
    isReadOnly = false;
    isPer10aChecked = true;
    isPer10aCheckedChemical = true;
    selectedProducerName =''
    currentFertCell: FertilizerUsage | null = null;
    currentChemicalCell: ChemicalUsage | null = null;
    currentLocalId: string | null = null;
    lastUsedUnitOfMeasure: 0 | 1 = 0;
    previousComId: string | null = null;
    previousPdId: string | null = null;
    previousPdTenantId : string | null = null;
    previousTenantId : string | null = null;
    previousSitemId : string | null = null;
    previousSeedlingId : string | null = null;
    showSanpu = false;
    sprayDilRatio = false;
    isChemRecommendedOnly = false;
    isFertRecommendedOnly = false;
    showChemCols = false;
    isTenantDisabled =  false;
    ingredientStr = '';
    isCultivationHistorySaved = false
    tempCultivationId: string | null = null
    tempGroupId: string | null = null
    sortOrder = true;
    private fertilizerUnits = [] as any[];
    private alertMessage = 'リクエストに失敗しました。\n'

    selectedTab: 'basic' | 'fertilizer' | 'work' | 'chemical' = 'basic'

    selectTab(tab: 'basic' | 'fertilizer' | 'work' | 'chemical'): void {
        if (this.selectedTab === 'basic' && tab !== 'basic') {
            const basicTabMessage = this.validateBasicTabData();
            if (basicTabMessage) {
                window.alert(basicTabMessage);
                return;
            }
        }
        this.selectedTab = tab
        if (tab === 'basic') {
            this.$nextTick(() => this.observePairDetailHeight());
        }
    }

    private hasTabData(level: 'dantai' | 'bukai' | 'producer' | 'tenant' | 'sitem'): boolean {
        const f = this.form;
        const hasCommonData =
            !!f.plantingId ||
            !!f.plantingName ||
            !!f.plantingStartDate ||
            !!f.plantingEndDate ||
            !!f.seedlingId ||
            !!f.seedlingQty ||
            !!f.seedlingUnitId ||
            !!f.agriFieldId ||
            !!f.agriFieldNum ||
            !!f.agriFieldBranchNum ||
            !!f.agriFieldUsageArea ||
            !!f.prevCrops ||
            !!f.cropType ||
            !!f.purTrns ||
            !!f.seedSd ||
            !!f.checkPeriod ||
            !!f.totalYield ||
            !!f.totalShpVol ||
            this.bulkWorkRecords.length > 0 ||
            this.bulkFertilizers.length > 0 ||
            this.bulkChemicals.length > 0 ||
            this.deletedWorkRecordBatchIds.length > 0 ||
            this.deletedFertilizerBatchIds.length > 0 ||
            this.deletedChemicalBatchIds.length > 0 ||
            this.selectedPairs.some(p => !!p.plantingId);
        if (level === 'dantai') {
            return hasCommonData || !!f.comId;
        }
    
        if (level === 'bukai') {
            return hasCommonData || (this.isPesticidePlan && !!f.pdId) || (!this.isPesticidePlan && !!f.sitemId);
        }

        if (level === 'producer') {
            return hasCommonData || !!f.pdTenantId;
        }

        return hasCommonData;
    }    

    getEmptyForm(): CvHistory {
        return {
            tenantId: null,
            comId: null,
            pdId: null,
            pdTenantId: null,
            sitemId: null,
            plantingId: null,
            plantingName: null,
            plantingStartDate: null,
            plantingEndDate: null,
            seedlingId: null,
            seedlingQty: null,
            seedlingUnitId: null,
            agriFieldId: null,
            agriFieldNum: null,
            agriFieldBranchNum: null,
            agriFieldUsageArea: null,
            totalAgriFieldUsageArea: null,
            remainingAgriFieldUsageArea: null,
            prevCrops: null,
            cropType: null,
            purTrns: null,
            seedSd: null,
            checkPeriod: null,
            totalYield: null,
            totalShpVol: null,
            created: null,
            createdUser: null,
            createdUserName: '',
            updatedUserName: '',
            updated: null,
            updatedLocal: null,
            updatedUser: null,
        };
    }

    get isPesticidePlan() : boolean {
        return this.$store.getters.loginUser.isPesticidePlan;
    }

    get pesticidePlanProducer() : boolean {
        return this.$store.getters.loginUser.pesticidePlanProducer;
    }

    get sehiTableColSpan(): number {
        const fertilizers = this.fertTabPairSelection === 'bulk' ? this.bulkFertilizers : this.mergedIndividualFertilizers;
        const maxUsage = Math.max(
            ...fertilizers.map((cvhf: CvhFertilizer) => cvhf.workUsages.length || 0),
            0
        );
        return maxUsage >= 5 ? maxUsage + 1 : 5;
    }

    get nouyakuTableColSpan(): number {
        const chemicals = this.chemTabPairSelection === 'bulk' ? this.bulkChemicals : this.mergedIndividualChemicals;
        const maxUsage = Math.max(
            ...chemicals.map((cvhc: CvhChemical) => cvhc.workUsages.length || 0),
            0
        );
        return maxUsage >= 5 ? maxUsage + 1 : 5;
    }

    mounted() {
        this.observePairDetailHeight();
    }

    beforeDestroy() {
        this.pairHeightObserver?.disconnect();
        this.pairHeightObserver = null;
    }

    private observePairDetailHeight() {
        this.pairHeightObserver?.disconnect();
        const target = this.$refs.pairDetailBody as HTMLElement | undefined;
        if (!target) return;
        this.pairHeightObserver = new ResizeObserver(() => {
            this.detailBodyHeight = target.offsetHeight || null;
        });
        this.pairHeightObserver.observe(target);
    }

    @Watch("showCvhModal")
    async handlePopupOpening() {
        this.isInitializing = true;
        const initToken = ++this.initSeq;
        this.form = this.getEmptyForm();
        this.previousTenantId = null;
        this.previousComId = null;
        this.previousPdId = null;
        this.previousPdTenantId = null;
        this.showSanpu = this.sprayDilRatio = this.isChemRecommendedOnly = this.isFertRecommendedOnly = false;
        this.cropFamicId = null;
        this.cropName = null;
        this.previousSitemId = null;
        this.previousSeedlingId = null;
        this.sitem = null;
        this.selectedPairs = [];
        this.activePairIndex = 0;
        this.editingOriginalPairKey = null;
        this.workTabPairSelection = 'bulk';
        this.bulkWorkRecords = [];
        this.deletedWorkRecordBatchIds = [];
        this.deletedWorkRecordExpectedUpdates.clear();
        this.fertTabPairSelection = 'bulk';
        this.bulkFertilizers = [];
        this.deletedFertilizerBatchIds = [];
        this.chemTabPairSelection = 'bulk';
        this.bulkChemicals = [];
        this.deletedChemicalBatchIds = [];
        this.snapshotBulkOriginals();
        try {
            if (this.showCvhModal) {
                this.$nextTick(() => this.observePairDetailHeight());
                this.tempCultivationId = this.id ? this.id : null
                this.tempGroupId = this.groupId ?? null
                const startupTasks: Promise<unknown>[] = [this.getFertilizerUnits(), this.getChemicalUnits(), this.getSeedlingUnits()];
                if (this.mode === 'edit' && this.groupId) {
                    startupTasks.push(this.getCvHistoryGroup());
                } else if (this.mode === 'edit' && this.tempCultivationId) {
                    startupTasks.push(this.getCvHistory());
                } else {
                    if (this.pesticidePlanProducer && !this.isPesticidePlan) {
                        this.form.pdTenantId = this.$store.getters.tenantId || null;
                        this.previousPdTenantId = this.form.pdTenantId;
                        this.form.comId = this.committeeId || null;
                        this.form.pdId = this.producerId || null;
                        this.form.sitemId = this.shippingItemId || null;
                        startupTasks.push(this.handleTenant(false));
                        startupTasks.push(this.getCollectionAndShippingGroups());
                        startupTasks.push(this.handleShippingItem(false));
                    } else if (this.isPesticidePlan) {
                        this.form.tenantId = this.$store.getters.tenantId || null;
                        this.previousTenantId = this.form.tenantId;
                        this.form.comId = this.committeeId || null;
                        this.form.pdId = this.producerId || null;
                        this.form.sitemId = this.shippingItemId || null;
                        startupTasks.push((async () => {
                            await Promise.all([this.getSettingDetails(), this.getCommittees(), this.getShippingItems(), this.handleShippingItem(false)]);
                            if (this.shippingItemId != null && this.committeeId != null && this.producerId != null) {
                                await this.handleProducer(false);
                            }
                        })());
                    }
                }
                await Promise.all(startupTasks);
            }
        } finally {
            if (initToken === this.initSeq) this.isInitializing = false;
        }
        this.$store.dispatch('doUpdateIsInputChanged', false);
    }

    private pairKeyOf(plantingId: string | null, agriFieldId: string | null): string | null {
        return plantingId && agriFieldId ? `${plantingId}_${agriFieldId}` : null;
    }

    private pairFromDetail(r: any): PairBasicData {
        return {
            existingCvId: r.id,
            updatedLocal: r.updatedLocal ?? null,
            tempPlantingKey: null,
            plantingId: r.plantingId ?? null,
            plantingName: r.plantingName ?? null,
            plantingStartDate: r.plantingStartDate ?? null,
            plantingEndDate: r.plantingEndDate ?? null,
            seedlingId: r.seedlingId ?? null,
            seedlingQty: r.seedlingQty ?? null,
            seedlingUnitId: r.seedlingUnitId ?? null,
            agriFieldId: r.agriFieldId ?? null,
            agriFieldName: r.agriFieldName ?? null,
            agriFieldNum: r.agriFieldNum ?? null,
            agriFieldBranchNum: r.agriFieldBranchNum ?? null,
            agriFieldUsageArea: r.agriFieldUsageArea ?? null,
            totalAgriFieldUsageArea: this.agriFields.find(af => af.id === r.agriFieldId)?.plantedArea ?? null,
            remainingAgriFieldUsageArea: null,
            prevCrops: r.prevCrops ?? null,
            cropFamicId: r.cropFamicId ?? null,
            cropName: r.cropName ?? null,
            workRecords: (r.workRecords ?? []).map((wr: WorkRecord) => ({ ...wr, processType: 'NOCHANGE' as const })),
            fertilizers: (r.fertilizers ?? []).map((f: CvhFertilizer) => ({
                ...f,
                workUsages: (f.workUsages ?? []).map(u => ({ ...u, processType: 'NOCHANGE' as const })),
            })),
            chemicals: (r.chemicals ?? []).map((c: CvhChemical) => ({
                ...c,
                workUsages: (c.workUsages ?? []).map(u => ({ ...u, processType: 'NOCHANGE' as const })),
            })),
            seedSd: r.seedSd ?? null,
            checkPeriod: r.checkPeriod ?? null,
            cropType: r.cropType ?? null,
            purTrns: r.purTrns ?? null,
            totalYield: r.totalYield ?? null,
            totalShpVol: r.totalShpVol ?? null,
            created: r.created ?? null,
            createdUserName: r.createdUserName ?? null,
            updated: r.updated ?? null,
            updatedUserName: r.updatedUserName ?? null,
        };
    }

    async autoLinkExistingPairs() {
        const pdTenantId = this.form.pdTenantId;
        const tenantId = this.form.tenantId;

        const candidates = this.selectedPairs
            .map((pair, index) => ({ pair, index }))
            .filter(({ pair }) => !pair.existingCvId && pair.plantingId && pair.agriFieldId);

        if (!pdTenantId || !tenantId || candidates.length === 0) {
            return;
        }

        const checkItems = candidates.map(({ pair }) => ({
            key: `${pair.plantingId}_${pair.agriFieldId}`,
            plantingId: pair.plantingId as string,
            agriFieldId: pair.agriFieldId as string,
        }));

        try {
            const payload = { pdTenantId, dantaiTenantId: tenantId, pairs: checkItems };
            const matches: Record<string, string> = await this.$apiClient.doRequest('post', `${this.baseUrl}/check_cv_history_existance`, payload);

            for (const { pair, index } of candidates) {
                const matchedCvId = matches[`${pair.plantingId}_${pair.agriFieldId}`];
                if (!matchedCvId) continue;
                const res = await this.$apiClient.doRequest('get', `${this.baseUrl}/${matchedCvId}`);
                const linkedPair = this.pairFromDetail(toCamel(res));
                this.selectedPairs.splice(index, 1, linkedPair);
                if (index === this.activePairIndex) {
                    this.applyPairToForm(linkedPair);
                }
            }
        } catch {
            this.$toasted.error('データの取得に失敗しました。');
        }
    }

    async getCvHistory() {
        this.isLoading = true;
        this.previousTenantId = null;
        this.previousComId = null;
        this.previousPdId = null;
        this.previousPdTenantId = null;
        this.previousSeedlingId = null;
        try {
            const rawRes = await this.$apiClient.doRequest('get', `${this.baseUrl}/${this.tempCultivationId}/group_view`);
            const res = toCamel(rawRes) as { groupId: string | null; records: any[]; workRecordBatches: any[]; fertilizerBatches: any[]; chemicalBatches: any[] };
            const records = (res.records ?? []).map(r => toCamel(r)) as any[];
            if (records.length === 0) {
                this.$toasted.error('データの取得に失敗しました。');
                return;
            }
            const first = records[0];
            this.tempGroupId = res.groupId ?? null;

            this.form = this.getEmptyForm();
            this.form.tenantId = first.tenantId ?? null;
            this.form.comId = first.comId ?? null;
            this.form.pdId = first.pdId ?? null;
            this.form.pdTenantId = first.pdTenantId ?? null;
            this.form.sitemId = first.sitemId ?? null;
            this.sitem = first.shippingItem ? first.shippingItem as ShippingItemDetail : null;

            this.selectedPairs = [this.pairFromDetail(first)];
            this.activePairIndex = 0;
            this.applyPairToForm(this.selectedPairs[0]);
            this.editingOriginalPairKey = this.selectedPairs[0].plantingId && this.selectedPairs[0].agriFieldId
                ? `${this.selectedPairs[0].plantingId}_${this.selectedPairs[0].agriFieldId}`
                : null;

            this.previousTenantId = this.form.tenantId;
            this.previousComId = this.form.comId;
            this.previousPdId = this.form.pdId;
            this.previousPdTenantId = this.form.pdTenantId;
            this.previousSitemId = this.form.sitemId;
            this.previousSeedlingId = this.form.seedlingId;

            if (this.pesticidePlanProducer) {
                await this.getCollectionAndShippingGroups();
            } else if (this.isPesticidePlan) {
                await Promise.all([this.getCommittees(), this.getShippingItems()]);
            }
            await Promise.all([this.getSettingDetails(), this.getSeedlings(), this.getAgriFields()]);
            await Promise.all([this.getWarehouseChemicals(), this.getWarehouseFertilizers(), this.getWorkItems()]);

            const batches = (res.workRecordBatches ?? []).map(r => toCamel(r)) as any[];
            this.bulkWorkRecords = this.buildBulkWorkRecordsFromBatches(batches);
            this.deletedWorkRecordBatchIds = [];
            this.deletedWorkRecordExpectedUpdates.clear();

            const fertBatches = (res.fertilizerBatches ?? []).map(r => toCamel(r)) as any[];
            this.bulkFertilizers = this.groupFertilizerBatches(fertBatches);
            this.deletedFertilizerBatchIds = [];

            const chemBatches = (res.chemicalBatches ?? []).map(r => toCamel(r)) as any[];
            this.bulkChemicals = this.groupChemicalBatches(chemBatches);
            this.deletedChemicalBatchIds = [];
            this.snapshotBulkOriginals();
        } catch (error) {
            this.$toasted.error('データの取得に失敗しました。');
        } finally {
            this.isLoading = false;
        }
    }

    async getShippingItems() {
        if (!this.form.tenantId) return;
        this.isLoading = true;
        try {
            const res = await this.$apiClient.doRequest('get',`/shipping_items/tenantId?with_hidden=true&tenant_id=${this.form.tenantId}`);
            this.shippingItems = (toCamel(res) as { shippingItems: ShippingItem[] }).shippingItems;
        } catch (error) {
            this.$toasted.error('データの取得に失敗しました。');
        } finally {
            this.isLoading = false;
        }
    }

    async getCollectionAndShippingGroups() {
        this.isLoading = true;
        try {
            const res = await this.$apiClient.doRequest('get', `${this.baseUrl}/collection_and_shipping_groups`);
            this.collectionAndShippingGroups = (toCamel(res) as { collectionAndShippingGroups: CollectionAndShippingGroup[] }).collectionAndShippingGroups;
            if (this.form.tenantId) {
                this.selectedCollectionAndShippingGroup =
                    this.collectionAndShippingGroups.find(group => group.tenantId === this.form.tenantId) || null;
            } else {
                this.selectedCollectionAndShippingGroup = this.collectionAndShippingGroups.find(group => group.tenantId === this.tenantId) || null;
                this.form.tenantId = this.tenantId || null;
                this.previousTenantId = this.form.tenantId;
                await this.getSettingDetails();
            }
            await Promise.all([this.getCommittees(), this.getShippingItems()]);
        } catch (error) {
            this.$toasted.error('データの取得に失敗しました。');
        } finally {
            this.isLoading = false;
        }
    }

    handleDantai(): void {
        if (this.hasTabData('dantai')) {
            if (!window.confirm("タブ「作業、肥料、農薬」の内容がクリアされます。よろしいですか？")) {
                this.form.tenantId = this.previousTenantId;
                return;
            }
        }
        this.previousTenantId = this.form.tenantId;
        this.inputChanged();
        this.clearFormData('dantai');
        if (!this.form.tenantId) return;
        let selectedGroup = null;
        for (const group of this.collectionAndShippingGroups) {
            group.isSelected = group.tenantId === this.form.tenantId;
            if (group.isSelected) {
                selectedGroup = group;
            }
        }
        this.selectedCollectionAndShippingGroup = selectedGroup;
        this.getCommittees();
        this.getShippingItems();
        this.getSettingDetails();
    }

    handleBukai(): void {
        if (this.hasTabData('bukai')) {
            if (!window.confirm("タブ「作業、肥料、農薬」の内容がクリアされます。よろしいですか？")) {
                this.form.comId = this.previousComId;
                return;
            }
        }
        this.previousComId = this.form.comId;
        this.inputChanged();
        this.clearFormData('bukai');
        if (!this.form.comId) return;
        const committee = this.committees.find(c => c.id === this.form.comId);
        this.producers = committee ? committee.producers : [];
    }

    async handleShippingItem(flag = false) {
        if (this.hasTabData('sitem') && flag) {
            if (!window.confirm("タブ「作業、肥料、農薬」の内容がクリアされます。よろしいですか？")) {
                this.form.sitemId = this.previousSitemId;
                return;
            }
        }
        this.previousSitemId = this.form.sitemId;
        this.seedlings = [];
         if(flag){
            this.inputChanged();
        }
        this.selectedPairs = [];
        this.activePairIndex = 0;
        this.editingOriginalPairKey = null;
        this.workTabPairSelection = 'bulk';
        this.fertTabPairSelection = 'bulk';
        this.chemTabPairSelection = 'bulk';
        this.resetPlanting();
        if (!this.form.sitemId) return;
        this.isLoading = true;
        try {
            const res = await this.$apiClient.doRequest('get', `/shipping_items/${this.form.sitemId}`);
            this.sitem = toCamel(res) as ShippingItemDetail;
            await this.getSeedlings();
        } catch (error) {
            this.$toasted.error('データの取得に失敗しました。');
        } finally {
            this.isLoading = false;
        }
    }

    clearFormData(level: 'dantai' | 'bukai' | 'producer' | 'tenant'): void {
        if (['dantai'].includes(level)) {
            this.selectedCollectionAndShippingGroup = null;
            this.form.comId = null;
            this.previousComId = null;
            this.shippingItems = [];
        }

        if (['dantai', 'bukai'].includes(level)) {
            if (this.isPesticidePlan) {
                this.form.pdId = null;
                this.previousPdId = null;
            }
            this.producers = [];
        }

        if (['dantai', 'bukai', 'producer'].includes(level)) {
            if (this.isPesticidePlan) {
                this.form.pdTenantId = null;
                this.previousPdTenantId = null;
            }
            this.tenants = [];
        }

        if (['dantai', 'bukai'].includes(level)) {
            this.form.sitemId = null;
            this.previousSitemId = null;
            this.sitem = null;
            this.seedlings = [];
        }
        if (['producer', 'tenant'].includes(level)) {
            this.seedlings = [];
            this.warehouseChemicals = [];
        }
        this.selectedPairs = [];
        this.activePairIndex = 0;
        this.editingOriginalPairKey = null;
        this.workTabPairSelection = 'bulk';
        this.fertTabPairSelection = 'bulk';
        this.chemTabPairSelection = 'bulk';
        this.resetPlanting();
        if (this.isPesticidePlan) {
            this.agriFields = [];
            this.workItems = [];
        }
    }

    async handleProducer(flag = false): Promise<void> {
        if (this.hasTabData('producer') && flag) {
            if (!window.confirm("タブ「作業、肥料、農薬」の内容がクリアされます。よろしいですか？")) {
                this.form.pdId = this.previousPdId;
                return;
            }
        }
        this.previousPdId = this.form.pdId;
        if(flag){
            this.inputChanged();
        }
        if(this.shippingItemId == null || flag){
            this.clearFormData('producer');
        }
        if (!this.form.comId || !this.form.pdId) return;

        const selectedCommittee = this.committees.find(c => c.id === this.form.comId);
        if (!selectedCommittee) return;

        const selectedProducer = selectedCommittee.producers.find(p => p.pdId === this.form.pdId);
        if (!selectedProducer) return;

        if (selectedProducer.pdNo == null || selectedProducer.pdNo == undefined) {
            window.alert('生産者の番号が登録されていません。');
        }
        this.tenants = (selectedProducer.tenants || []).filter(t => !t.deleted);
        this.form.pdTenantId = this.tenants.length === 1 ? this.tenants[0].id : null;
        this.previousPdTenantId = this.form.pdTenantId;
        if (this.form.pdTenantId) await this.handleTenant();
    }

    async handleTenant(flag = false): Promise<void> {
        if (this.hasTabData('tenant') && flag) {
            if (!window.confirm("タブ「作業、肥料、農薬」の内容がクリアされます。よろしいですか？")) {
                this.form.pdTenantId = this.previousPdTenantId;
                return;
            }
        }
        this.previousPdTenantId = this.form.pdTenantId;
         if(flag){
            this.inputChanged();
        }
        if(this.shippingItemId == null || flag){
            this.clearFormData('tenant');
        }
        if (!this.form.pdTenantId) return;
        await Promise.all([
            this.getSeedlings(),
            this.getAgriFields(),
            this.getWorkItems(),
            this.getWarehouseChemicals(),
            this.getWarehouseFertilizers()
        ]);
    }

    async getAgriFields() {
        if (!this.form.pdTenantId) return;
        this.isLoading = true;
        try {
            const res = await this.$apiClient.doRequest('get', `/agri_fields?with_hidden=true&tenant_id=${this.form.pdTenantId}`);
            this.agriFields = (toCamel(res) as { agriFields: AgriField[] }).agriFields;
        } catch {
            this.$toasted.error('データの取得に失敗しました。');
        } finally {
            this.isLoading = false;
        }
    }

    async getWorkItems() {
        if (!this.form.pdTenantId) return;
        this.isLoading = true;
        try {
            const res = await this.$apiClient.doRequest('get', `/work_items?with_hidden=true&tenant_id=${this.form.pdTenantId}`);
            this.workItems = (toCamel(res) as { workItems: WorkItem[] }).workItems;
        } catch {
            this.$toasted.error('データの取得に失敗しました。');
        } finally {
            this.isLoading = false;
        }
    }

    resetPlanting(flag = false) {
        if (flag && !window.confirm("タブ「作業、肥料、農薬」の内容がクリアされます。よろしいですか？")) {
            return;
        }
        this.form.plantingId = null;
        this.form.plantingName = null;
        this.form.plantingStartDate = null;
        this.form.plantingEndDate = null;
        this.form.seedlingId = null;
        this.form.seedlingQty = null;
        this.form.seedlingUnitId = null;
        this.form.agriFieldId = null;
        this.form.agriFieldNum = null;
        this.form.agriFieldBranchNum = null;
        this.form.agriFieldUsageArea = null;
        this.form.totalAgriFieldUsageArea = null;
        this.form.remainingAgriFieldUsageArea = null;
        this.form.prevCrops = null;
        this.form.cropType = null;
        this.form.purTrns = null;
        this.form.seedSd = null;
        this.form.checkPeriod = null;
        this.form.totalYield = null;
        this.form.totalShpVol = null;
        this.form.updatedLocal = null;
        this.form.created = null;
        this.form.createdUserName = null;
        this.form.updated = null;
        this.form.updatedUserName = null;
        this.bulkWorkRecords = [];
        this.deletedWorkRecordBatchIds = [];
        this.deletedWorkRecordExpectedUpdates.clear();
        this.bulkFertilizers = [];
        this.deletedFertilizerBatchIds = [];
        this.bulkChemicals = [];
        this.deletedChemicalBatchIds = [];
        this.snapshotBulkOriginals();
        this.cropFamicId = null;
        this.cropName = null;
        this.previousSeedlingId = null;
        this.inputChanged();
    }

    async getRemaniningAreaCall (){
        this.form.totalAgriFieldUsageArea = this.agriFields?.find((af: { id: string | null; }) => af.id === this.form.agriFieldId)?.plantedArea ?? null;
        if(this.form.agriFieldId && this.form.plantingStartDate && this.form.plantingEndDate && this.form.plantingStartDate < this.form.plantingEndDate && this.form.plantingId == null){
            await this.getRemainingAreas([this.form.agriFieldId], this.form.plantingStartDate, this.form.plantingEndDate);
        } else {
            this.form.remainingAgriFieldUsageArea = null;
        }
    }

    get mergedIndividualWorkRecords(): WorkRecord[] {
        const pair = this.workTabPairSelection !== 'bulk' ? this.selectedPairs[this.workTabPairSelection] : undefined;
        const pairWorkRecords = pair?.workRecords ?? [];
        const bulkById = new Map(this.bulkWorkRecords.map(b => [b.id, b]));
        const merged: WorkRecord[] = [];
        for (const wr of pairWorkRecords) {
            if (!wr.workLinkId) {
                merged.push(wr);
                continue;
            }
            if (this.deletedWorkRecordBatchIds.includes(wr.workLinkId)) {
                continue;
            }
            const bulk = bulkById.get(wr.workLinkId);
            if (!bulk || bulk.processType === 'NOCHANGE') {
                merged.push(wr);
                continue;
            }
            merged.push({
                ...wr,
                workDate: bulk.workDate,
                workItemId: bulk.workItemId,
                workItemName: bulk.workItemName,
                editable: false,
                processType: bulk.processType,
            });
        }
        for (const bulk of this.bulkWorkRecords) {
            if (bulk.id.startsWith('temp::')) {
                merged.push({
                    id: bulk.id,
                    workDate: bulk.workDate,
                    workItemId: bulk.workItemId,
                    workItemName: bulk.workItemName,
                    status: null,
                    editable: false,
                    updated: null,
                    processType: bulk.processType,
                    workLinkId: null,
                });
            }
        }
        if (!this.sortField) {
            return [...merged].sort((a, b) => this.compareDatesDescending(a.workDate, b.workDate));
        }
        return this.sortWorkItems(merged);
    }

    get hasPendingBulkWorkRecordChanges(): boolean {
        return this.bulkWorkRecords.some(b => b.processType !== 'NOCHANGE')
            || this.deletedWorkRecordBatchIds.length > 0;
    }

    selectWorkTabPair(selection: string) {
        this.workTabPairSelection = selection === 'bulk' ? 'bulk' : Number(selection);
    }

    addWorkRecordBatch() {
        this.bulkWorkRecords.unshift({
            id: `temp::${Date.now()}`,
            workLinkId: null,
            workItemId: null,
            workItemName: null,
            workDate: '',
            editable: true,
            hasProtectedResourceData: false,
            processType: 'ADD',
        });
        this.inputChanged();
    }

    deleteWorkRecordBatch(index: number) {
        const batch = this.bulkWorkRecords[index];
        if (!batch) return;
        if (batch.id && !batch.id.startsWith('temp::')) {
            this.deletedWorkRecordBatchIds.push(batch.id);
            this.rememberDeletedWorkRecordLock(batch);
        }
        this.bulkWorkRecords.splice(index, 1);
        const key = this.workRecordKey(batch);
        if (key) {
            this.cascadeDeleteUsagesForWorkRecord(key);
        }
        this.inputChanged();
    }

    private cascadeDeleteUsagesForWorkRecord(workRecordId: string): void {
        if (!workRecordId) return;
        const touchedFerts = new Set<CvhFertilizer>();
        this.bulkFertilizers.forEach(fert => {
            const before = fert.workUsages.length;
            fert.workUsages = fert.workUsages.filter(usage => {
                if (usage.workRecordId !== workRecordId) return true;
                if (usage.id && !usage.id.startsWith('temp::')) {
                    this.deletedFertilizerBatchIds.push(usage.id);
                    this.rememberDeletedUsageLock(usage, usage.shares);
                }
                return false;
            });
            if (fert.workUsages.length !== before) touchedFerts.add(fert);
        });
        const touchedChems = new Set<CvhChemical>();
        this.bulkChemicals.forEach(chem => {
            const before = chem.workUsages.length;
            chem.workUsages = chem.workUsages.filter(usage => {
                if (usage.workRecordId !== workRecordId) return true;
                if (usage.id && !usage.id.startsWith('temp::')) {
                    this.deletedChemicalBatchIds.push(usage.id);
                    this.rememberDeletedUsageLock(usage, usage.pairShares);
                }
                return false;
            });
            if (chem.workUsages.length !== before) touchedChems.add(chem);
        });
        this.bulkFertilizers = this.bulkFertilizers.filter(f => f.workUsages.length > 0 || !touchedFerts.has(f));
        this.bulkChemicals = this.bulkChemicals.filter(c => c.workUsages.length > 0 || !touchedChems.has(c));
    }

    handleWorkRecordDateChange(batch: WorkRecordBatch) {
        if (batch.processType === 'NOCHANGE') {
            batch.processType = 'UPDATE';
        }
        const key = this.workRecordKey(batch);
        if (batch.workDate && key) {
            this.propagateWorkRecordDate(key, batch.workDate);
        }
        this.inputChanged();
    }

    handleWorkRecordItemChange(batch: WorkRecordBatch) {
        const selectedWorkItem = this.workItems.find(wi => wi.id === batch.workItemId);
        batch.workItemName = selectedWorkItem ? selectedWorkItem.name : null;
        if (batch.processType === 'NOCHANGE') {
            batch.processType = 'UPDATE';
        }
        this.inputChanged();
    }

    resetWorkRecordBatch(index: number): void {
        const batch = this.bulkWorkRecords[index];
        if (!batch) return;
        batch.workItemName = null;
        batch.workItemId = null;
        if (batch.processType === 'NOCHANGE') {
            batch.processType = 'UPDATE';
        }
        this.inputChanged();
    }

    private confirmPairChange(): boolean {
        if (this.selectedPairs.length === 0) return true;
        return window.confirm('保存していないデータは破棄されますが、よろしいですか？');
    }

    private async syncGroupForPairChange(): Promise<void> {
        this.bulkWorkRecords = [];
        this.deletedWorkRecordBatchIds = [];
        this.deletedWorkRecordExpectedUpdates.clear();
        this.workTabPairSelection = 'bulk';
        this.bulkFertilizers = [];
        this.deletedFertilizerBatchIds = [];
        this.fertTabPairSelection = 'bulk';
        this.bulkChemicals = [];
        this.deletedChemicalBatchIds = [];
        this.chemTabPairSelection = 'bulk';
        this.snapshotBulkOriginals();

        const realPairs = this.selectedPairs.filter(p => p.plantingId && p.agriFieldId);
        if (realPairs.length === 0) return;
        const allReal = realPairs.length === this.selectedPairs.length;
        const cvIds = realPairs.map(p => p.existingCvId).filter((id): id is string => !!id);

        if (allReal && cvIds.length === realPairs.length) {
            try {
                const rawRes = await this.$apiClient.doRequest('post', `${this.baseUrl}/find_group`, { cvIds });
                const res = toCamel(rawRes) as { groupId: string | null };
                if (res.groupId) {
                    this.tempGroupId = res.groupId;
                    this.mode = 'edit';
                    await this.getCvHistoryGroup();
                    this.$store.dispatch('doUpdateIsInputChanged', false);
                    return;
                }
            } catch {
                this.$toasted.error('データの取得に失敗しました。');
            }
        }

        if (allReal && realPairs.length === 1) {
            this.tempGroupId = null;
            const cvId = realPairs[0].existingCvId;
            if (cvId) {
                await this.loadBulkDataForPair(cvId);
            } else {
                await this.loadBulkDataForNewPair(realPairs[0]);
            }
        } else {
            await this.fetchWorkRecordsForPairs(realPairs);
        }
    }

    private async loadBulkDataForNewPair(pair: PairBasicData): Promise<void> {
        try {
            const rawRes = await this.$apiClient.doRequest('post', `${this.baseUrl}/pairs_work_records`, {
                pairs: [{ plantingId: pair.plantingId as string, agriFieldId: pair.agriFieldId as string }],
                sitemId: this.form.sitemId,
                tenantId: this.form.tenantId,
            });
            const res = toCamel(rawRes) as { items: { plantingId: string; agriFieldId: string; workRecords: (WorkRecord & { hasProtectedResourceData?: boolean })[]; fertilizers: CvhFertilizer[]; chemicals: CvhChemical[] }[] };
            const item = res.items[0];
            if (!item) return;
            await this.getWorkItems();
            this.bulkWorkRecords = (item.workRecords ?? []).map(wr => ({
                id: wr.id,
                workLinkId: null,
                workItemId: wr.workItemId,
                workItemName: this.workItems.find(wi => wi.id === wr.workItemId)?.name ?? null,
                workDate: wr.workDate,
                editable: wr.editable ?? true,
                hasProtectedResourceData: wr.hasProtectedResourceData ?? false,
                processType: 'NOCHANGE' as const,
                members: [{ plantingId: pair.plantingId, agriFieldId: pair.agriFieldId as string, workRecordId: wr.id, updated: wr.updated ?? null }],
            }));
            this.bulkFertilizers = (item.fertilizers ?? []).map(f => ({
                ...f,
                workUsages: (f.workUsages ?? []).map(u => ({ ...u, processType: 'NOCHANGE' as const })),
            }));
            this.bulkChemicals = (item.chemicals ?? []).map(c => ({
                ...c,
                workUsages: (c.workUsages ?? []).map(u => ({ ...u, processType: 'NOCHANGE' as const })),
            }));
            this.snapshotBulkOriginals();
        } catch {
            this.$toasted.error('データの取得に失敗しました。');
        }
    }

    private async loadBulkDataForPair(cvId: string): Promise<void> {
        try {
            const rawRes = await this.$apiClient.doRequest('get', `${this.baseUrl}/${cvId}/group_view`);
            const res = toCamel(rawRes) as { groupId: string | null; workRecordBatches: any[]; fertilizerBatches: any[]; chemicalBatches: any[] };
            this.tempGroupId = res.groupId ?? null;
            await this.getWorkItems();
            const batches = (res.workRecordBatches ?? []).map(r => toCamel(r)) as any[];
            this.bulkWorkRecords = this.buildBulkWorkRecordsFromBatches(batches);
            const fertBatches = (res.fertilizerBatches ?? []).map(r => toCamel(r)) as any[];
            this.bulkFertilizers = this.groupFertilizerBatches(fertBatches);
            const chemBatches = (res.chemicalBatches ?? []).map(r => toCamel(r)) as any[];
            this.bulkChemicals = this.groupChemicalBatches(chemBatches);
            this.snapshotBulkOriginals();
        } catch {
            this.$toasted.error('データの取得に失敗しました。');
        }
    }

    private async fetchWorkRecordsForPairs(pairs: PairBasicData[]): Promise<void> {
        const pairKeys = pairs.map(p => ({ plantingId: p.plantingId as string, agriFieldId: p.agriFieldId as string }));
        try {
            const rawRes = await this.$apiClient.doRequest('post', `${this.baseUrl}/pairs_work_records`, { pairs: pairKeys, sitemId: this.form.sitemId, tenantId: this.form.tenantId });
            const res = toCamel(rawRes) as { items: { plantingId: string; agriFieldId: string; workRecords: WorkRecord[]; fertilizers: CvhFertilizer[]; chemicals: CvhChemical[] }[] };
            const byKey = new Map(res.items.map(item => [`${item.plantingId}_${item.agriFieldId}`, item]));
            for (const pair of this.selectedPairs) {
                const key = `${pair.plantingId}_${pair.agriFieldId}`;
                const item = byKey.get(key);
                if (!item) continue;
                pair.workRecords = (item.workRecords ?? []).map(wr => ({ ...wr, processType: 'NOCHANGE' as const }));
                pair.fertilizers = (item.fertilizers ?? []).map(f => ({
                    ...f,
                    workUsages: (f.workUsages ?? []).map(u => ({ ...u, processType: 'NOCHANGE' as const })),
                }));
                pair.chemicals = (item.chemicals ?? []).map(c => ({
                    ...c,
                    workUsages: (c.workUsages ?? []).map(u => ({ ...u, processType: 'NOCHANGE' as const })),
                }));
            }
        } catch {
            this.$toasted.error('データの取得に失敗しました。');
        }
    }

    async getPreviousCrops() {
        if (!this.form.agriFieldId) return;
        this.isLoading = true;
        try {
            const res = await this.$apiClient.doRequest(
                'get',
                `/agri_fields/${this.form.agriFieldId}/previous_crops${this.form.plantingId ? `?planting_id=${this.form.plantingId}` : ''}`
            );
            this.form.prevCrops = res.prevCrops || null;
        } catch {
            this.$toasted.error('データの取得に失敗しました。');
        } finally {
            this.isLoading = false;
        }
    }

    handleAgriField(): void {
        this.inputChanged();
        if (!this.form.agriFieldId) return;
        this.getPreviousCrops();
        this.getRemaniningAreaCall();
    }

    private async getRemainingAreas(agriFields: string[], workStartDate: Date, workEndDate: Date) {
        if (agriFields.length !== 0) {
            const agriFieldsParam = agriFields.map(id => `agri_fields=${id}`).join('&')
            const workStartDateParam = `work_start_date=${this.form.plantingStartDate}`
            const workEndDateParam = `work_end_date=${this.form.plantingEndDate}`
            const excludePlantingParam = this.form.plantingId ? this.form.plantingId : '';
            const uri = `/agri_fields/remaining_areas?${agriFieldsParam}&${workStartDateParam}&${workEndDateParam}&${excludePlantingParam}`
            const encodedUri = encodeURI(uri)
            const resAgriFields: AgriFieldOnPlantingResponse[] = await this.$apiClient.doRequest('get', encodedUri)
            this.form.remainingAgriFieldUsageArea = resAgriFields.find(af => af.id === this.form.agriFieldId)?.remaining_area ?? null
            return resAgriFields
        } else {
                return []
        }
    }

    private updateWorkStartAndEndDate() {
       if (!this.form.plantingId && !this.isNewPlantingPeriodValid) {
           this.form.agriFieldUsageArea = null;
       }
       this.getRemaniningAreaCall();
    }

    get filteredProducers() {
        return this.producers.filter(p => this.form.pdId == p.pdId || !p.isHidden)
    }

    get filteredSeedlings() {
        return this.seedlings.filter(s => this.form.seedlingId == s.id || !s.isHidden)
    }

    get displayCropName(): string {
        if (this.cropName) return this.cropName;
        if (!this.form.plantingId) return this.sitem?.cropItemName ?? '';
        return '';
    }

    get filteredAgriFields() {
        return this.agriFields.filter(af => this.form.agriFieldId == af.id || !af.isHidden)
    }

    get filteredShippingItems() {
        return this.shippingItems.filter(si => si.id === this.form.sitemId || (si.comId === this.form.comId && !si.isHidden));
    }

    get filteredWorkItems() {
        return this.workItems;
    }

    formatPeriod(start: string, end: string): string {
        return start && end ? `${start}〜${end}` : '-';
    }

    get cvMethodLabel(): string | null {
        const cvMethod = this.sitem?.cvMethod;
        if (cvMethod === null || cvMethod === undefined) return null;
        return ({ 0: '慣行', 1: '特栽', 2: '化学農薬・化学肥料不使用' } as Record<number, string>)[cvMethod] ?? null;
    }

    get agriFieldNumber(): string | null {
        const { agriFieldNum: num, agriFieldBranchNum: branch, comId, pdId } = this.form;
        if (!num || !branch) return "---";
        const committee = this.committees.find(c => c.id === comId);
        const producer = committee?.producers.find(p => p.pdId === pdId);
        if (!producer?.pdNo) return "---";
        const branchStr = String(Number(branch));
        return `${String(producer.pdNo).padStart(4, "0")}${String(num).padStart(3, "0")}-${branchStr}`;
    }

    get prevCrops(): string {
        return this.form.prevCrops?.trim() ? this.form.prevCrops : '---';
    }

    async getCommittees() {
        if (!this.form.tenantId) return;
        this.isLoading = true;
        try {
            const res = await this.$apiClient.doRequest('get', `${this.baseUrl}/affiliated_committees/${this.form.tenantId}`);
            this.committees = (toCamel(res) as { committees: Committee[] }).committees;
            this.showChemCols = res.showChemCols;
            if (this.pesticidePlanProducer && !this.form.pdId) {
                this.form.pdId = res.pdId || null;
                this.previousPdId = this.form.pdId;
            }
            let committee = this.committees.find(c => c.id === this.form.comId);
             if (!committee) {
                const res = await this.$apiClient.doRequest('get', `${this.baseUrl}/affiliated_committees_list/${this.form.tenantId}`);
                const comiittesFromApi = (res?.committees || []).find((c :any) => c.id === this.form.comId);
                if (comiittesFromApi) {
                this.committees.push(comiittesFromApi);
                this.filteredCommittees
                committee = this.committees.find(c => c.id === this.form.comId);
                }
            }
            this.producers = committee ? committee.producers : [];
            let selectedProducer = this.producers.find(p => p.pdId === this.form.pdId);
            if(!selectedProducer){
               const res = await this.$apiClient.doRequest('get', `${this.baseUrl}/affiliated_producers/${this.form.tenantId}`);  
                  const producerFromApi = (res?.producersListTenants || []).find((p :any) => p.pdId === this.form.pdId);
                if (producerFromApi) {
                this.producers.push(producerFromApi);
                selectedProducer = producerFromApi;
                }
            }
            this.tenants = selectedProducer?.tenants || [];
            this.validateTenantSelection();
        } catch (error) {
            this.$toasted.error('データの取得に失敗しました。');
        } finally {
            this.isLoading = false;
        }
    }

    async getSeedlings() {
        if (!this.form.pdTenantId || !this.sitem?.cropItemId) return;
        this.isLoading = true;
        try {
            const res = await this.$apiClient.doRequest('get', `/seedlings/${this.form.pdTenantId}/${this.sitem.cropItemId}`);
            this.seedlings = (toCamel(res) as { seedlings: Seedling[] }).seedlings;
            this.cropFamicId = this.seedlings.find(s => s.id === this.form.seedlingId)?.cropFamicId ?? null;
        } catch (error) {
            this.$toasted.error('データの取得に失敗しました。');
        } finally {
            this.isLoading = false;
        }
    }

    async handleSeedling() {
        if (this.previousSeedlingId && this.previousSeedlingId !== this.form.seedlingId && this.bulkChemicals.length > 0 && !window.confirm("タブ「農薬」の内容がクリアされます。よろしいですか？")) {
            this.form.seedlingId = this.previousSeedlingId;
            return;
        }
        if (this.previousSeedlingId && this.form.seedlingId) {
            this.bulkChemicals.forEach(chem => {
                chem.workUsages.forEach(wu => {
                    if (wu.id && !wu.id.startsWith('temp::')) {
                        this.deletedChemicalBatchIds.push(wu.id);
                        this.rememberDeletedUsageLock(wu, wu.pairShares);
                    }
                });
            });
            this.bulkChemicals = [];
        }
        this.previousSeedlingId = this.form.seedlingId;
        this.cropFamicId = this.seedlings.find(s => s.id === this.form.seedlingId)?.cropFamicId || null;
        this.cropName = null;
        if (this.form.seedlingId) {
            try {
                const res = await this.$apiClient.doRequest('get', `/seedlings/${this.form.seedlingId}`);
                this.cropName = res.cropFamic?.name || null;
            } catch {
                this.$toasted.error('データの取得に失敗しました。');
            }
        }
        this.inputChanged();
    }

    async getSeedlingUnits() {
        this.isLoading = true;
        try {
            const res = await this.$apiClient.doRequest('get', '/plantings/seedling_units');
            this.seedlingUnits = (toCamel(res) as { seedlingUnits: SeedlingUnit[] }).seedlingUnits;
        } catch (error) {
            this.$toasted.error('データの取得に失敗しました。');
        } finally {
            this.isLoading = false;
        }
    }

    get filteredCommittees() {
        return this.committees.filter(c => this.form.comId == c.id || !c.isHidden)
    }

    @Emit('inputChanged')
    inputChanged() {
        if (store.getters.isInputChanged == false) {
            this.$store.dispatch('doUpdateIsInputChanged', true);
        }
    }

    closeModal() {
        const proceed = !store.getters.isInputChanged || window.confirm("変更内容を破棄します。よろしいですか？");
        if (proceed) {
            this.selectedTab = 'basic';
            this.collectionAndShippingGroups = [];
            this.selectedCollectionAndShippingGroup = null;
            this.clearFormData('dantai');
            this.seedlings = [];
            this.agriFields = [];
            this.warehouseChemicals = [];
            this.warehouseFertilizers = [];
            this.workItems = [];
            this.form = this.getEmptyForm();
            this.previousTenantId = null;
            this.previousComId = null;
            this.previousPdId = null;
            this.previousPdTenantId = null;
            this.previousSitemId = null;
            this.previousSeedlingId = null;
            this.showSanpu = this.sprayDilRatio = this.isChemRecommendedOnly = this.isFertRecommendedOnly = false;
            this.tempCultivationId = null
            store.dispatch("doUpdateIsInputChanged", false);
            this.$emit('update:showCvhModal', false);
        }
        if(this.isCultivationHistorySaved === true){
        this.isCultivationHistorySaved = false
        this.tempCultivationId = null
        this.$emit('update:isRefresh', true);
        }
    }

    initializeFertilizers(fertilizers: CvhFertilizer[]) {
        return fertilizers.map(f => ({
            id: f.resolvedFertilizerId ?? f.fertilizerId,
            warehouseId: f.warehouseName === '未管理' ? null : f.warehouseId
        }));
    }

    initializeChemicals(chemicals: CvhChemical[]) {
        return chemicals.map(c => ({
            id: c.chemicalId,
            warehouseId: c.warehouseName === '未管理' ? null : c.warehouseId,
        }));
    }

    openFertilizerSelectionModal() {
        if (!this.form.tenantId) {
            return window.alert('集出荷団体を選択してください。');
        }
        if (!this.form.pdTenantId) {
            return window.alert('生産者の対象テナントを選択してください。');
        }
        if (!this.form.sitemId) {
            return window.alert('出荷品目を選択してください。');
        }
        this.showFertilizerSelectionModal = true;
    }

    normalizeWarehouse(v: { warehouseId: any; warehouseName?: string; }) {
        return v.warehouseName === '未管理' || v.warehouseId == null ? 'UNMANAGED' : v.warehouseId;
    }

    convertToKg(value: number, unit: string):number{
        const fertUnit = this.fertilizerUnits?.find(u => u.name === unit);
        return value * fertUnit.weight_per_unit;
    }

    get currentDisplayFertilizers(): CvhFertilizer[] {
        return this.fertTabPairSelection === 'bulk' ? this.bulkFertilizers : this.mergedIndividualFertilizers;
    }

    get currentDisplayChemicals(): CvhChemical[] {
        return this.chemTabPairSelection === 'bulk' ? this.bulkChemicals : this.mergedIndividualChemicals;
    }

    get currentDisplayFertilizerArea(): number | null {
        if (this.fertTabPairSelection === 'bulk') return this.bulkTotalAgriFieldUsageArea;
        const pair = this.selectedPairs[this.fertTabPairSelection];
        return pair ? pair.agriFieldUsageArea : null;
    }

    get sehiTableTotal(): number {
        const area = this.currentDisplayFertilizerArea;
        if (!area || area <= 0) return 0;
        const total = this.currentDisplayFertilizers.reduce((sum, fert) => {
            const chissoSum = (fert.workUsages || []).reduce((acc, usage) => {
                if (usage?.usageQty != null && fert.chemRetio != null && usage.usageUnit) {
                    const qtyKg = this.convertToKg(usage.usageQty, usage.usageUnit);
                    const per10aQty = usage.unitOfMeasure === 1 
                        ? qtyKg
                        : (qtyKg / area) * 10;
                    const rounded = Math.round(((per10aQty * fert.chemRetio) / 100) * 100) / 100;    
                    return acc + rounded;
                }
                return acc;
            }, 0);
            return sum + chissoSum;
        }, 0);
        return total;
    }

    formatUsageQty(usage: WorkUsage, areaOverride?: number | null): string {
        const area = areaOverride !== undefined ? areaOverride : this.form.agriFieldUsageArea;
        if (!usage || usage.usageQty == null || !usage.usageUnit || !area) return '---';

        const { usageQty: q, usageUnit: u ,unitOfMeasure : m} = usage;
        return this.isPer10aChecked
        ? `${this.formatNumberWithCommas(m === 1 ? q : (q / area * 10))} ${u}/10a`
        : `${this.formatNumberWithCommas(m === 1 ? (q * area / 10 ): q )} ${u}${ this.isPer10aChecked ?'/10a' : ''}`;
    }

    formatChisso(usage: WorkUsage, r?: number | null, areaOverride?: number | null): string {
        const area = areaOverride !== undefined ? areaOverride : this.form.agriFieldUsageArea;
        if (!usage || usage.usageQty == null || !usage.usageUnit || r == null || !area) return '---';

        const kg = this.convertToKg(usage.usageQty, usage.usageUnit);
        const kchemRetio = kg * r/100

        return this.isPer10aChecked
        ? `${this.formatNumberWithCommas(usage.unitOfMeasure === 1 ? kchemRetio : (kchemRetio / area * 10))} kg/10a`
        : `${this.formatNumberWithCommas(usage.unitOfMeasure === 1 ? (kchemRetio * area / 10 ): kchemRetio )} kg`;
    }

    formatNumberWithCommas = (v: number) => (v % 1 ? v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : v.toLocaleString());

    async getSettingDetails() {
        if (!this.form.tenantId) return;
        try {
            this.showSanpu = this.sprayDilRatio = this.isChemRecommendedOnly = this.isFertRecommendedOnly = false;
            const res = await this.$apiClient.doRequest('get', `/setting_screen/${this.form.tenantId}`);
            this.showSanpu = this.sprayDilRatio = res.sprayDilRatio !== false;
            this.isChemRecommendedOnly = res.pdCvhChemRecOnly === true;
            this.isFertRecommendedOnly = res.pdCvhFertRecOnly === true;
        } catch {
            this.$toasted.error('データの取得に失敗しました。');
        }
    }

    openPesticideSelectionModal() {
        if (!this.form.tenantId) {
            return window.alert('集出荷団体を選択してください。');
        }
        if (!this.form.pdTenantId) {
            return window.alert('生産者の対象テナントを選択してください。');
        }
        if (!this.form.sitemId) {
            return window.alert('出荷品目を選択してください。');
        }
        this.isShowPesticideSelectionModal = true;
    }

    openFertilizerModal(): void {
        this.showFertilizerModal = true
        this.isNewFertilizer = true
    }

    async onFertilizerCreated(id: string | null) {
        if (id) {
            try {
                const r = await this.$apiClient.doRequest("get", `/fertilizers/${id}`);
                this.bulkFertilizers.push({
                    fertilizerId: r.id,
                    fertilizerName: r.name,
                    maker: r.maker ?? null,
                    chemRetio: r.kchemRetio,
                    warehouseId: r.warehouseId ?? null,
                    warehouseName: r.warehouseName ?? null,
                    workUsages: [],
                    ownerClass: this.isPesticidePlan ? 0 : (this.pesticidePlanProducer  ? (r.ownerClass != null ? r.ownerClass : 1) : 1)
                });
            } catch {
                this.$toasted.error('データの取得に失敗しました。');
            }
        }
        this.showFertilizerModal = this.isNewFertilizer = false;
        this.inputChanged();
    }

    onSeedlingCreated(id: string | null) {
        this.getSeedlings();
        this.inputChanged();
    }

    openSeedlingModal(): void {
        this.isNewSeedling = true;
        this.selectedProducerName = this.producers.find(item => item.pdId === this.form.pdId)?.pdName ?? '';
        this.form.pdTenantId ? this.showSeedlingModal = true : window.alert('生産者の対象テナントを選択してから、品種を登録してください。');
        this.$store.dispatch('doUpdateIsInputChanged', false);
    }

    openPlantingSelectionModal() {
        if (!this.form.pdTenantId) {
            window.alert('生産者の対象テナントを選択してから、作付名を選択してください。');
            return;
        }
        if (!this.form.sitemId) {
            window.alert('出荷品目を選択してから、作付名を選択してください。');
            return;
        }
        this.showSelectPlantingsAndAgriFields = true;
    }

    handleCheckPeriodChange(): void {
        this.inputChanged();
        if(this.form.checkPeriod == 0 || this.form.checkPeriod == null){
        this.form.totalYield = null;
        }
        if(this.form.checkPeriod == 0 || this.form.checkPeriod == 1 || this.form.checkPeriod == null){
        this.form.totalShpVol = null;
        }
    }

    private sortField: string | null = null;

    private compareWorkValues(aValue: any, bValue: any): number {
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        const aNum = Number(aValue);
        const bNum = Number(bValue);
        const isANum = !isNaN(aNum);
        const isBNum = !isNaN(bNum);

        if (isANum && isBNum) {
            return this.sortOrder ? aNum - bNum : bNum - aNum;
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return this.sortOrder
                ? aValue.localeCompare(bValue, 'ja')
                : bValue.localeCompare(aValue, 'ja');
        }

        return this.sortOrder
            ? String(aValue).localeCompare(String(bValue), 'ja')
            : String(bValue).localeCompare(String(aValue), 'ja');
    }

    private sortWorkItems<T extends { workDate: any; workItemName: any }>(items: T[]): T[] {
        if (!this.sortField) return items;
        const field = this.sortField;
        return [...items].sort((a, b) => this.compareWorkValues(
            field === 'date' ? a.workDate : a.workItemName,
            field === 'date' ? b.workDate : b.workItemName,
        ));
    }

    private sort(fieldName: string): void {
        this.sortOrder = !this.sortOrder;
        this.sortField = fieldName;

        if (this.workTabPairSelection === 'bulk') {
            this.bulkWorkRecords = this.sortWorkItems(this.bulkWorkRecords);
        }
      }

    deleteForm() {
        const deletablePairs = this.selectedPairs.filter(p => p.existingCvId);
        if (deletablePairs.length === 0) return;
        if (!window.confirm(
            `現在選択中の全ての栽培履歴を削除します。よろしいですか？\n\n` +
            `但し、\n` +
            `・作業記録は、削除されません。\n` +
            `・作業記録と紐づいている肥料、農薬は、削除されません。\n` +
            `・日付を設定していない肥料、農薬は、削除されません。`
        )) return;
        this.isLoading = true;
        const payload = {
            items: deletablePairs.map(p => ({ id: p.existingCvId as string, updated: p.updatedLocal })),
        };
        this.$apiClient.doRequest('post', `${this.baseUrl}/bulk_delete`, payload).then((response: any) => {
            if (response.message) {
                let alertText = response.message;
                if (response.lockFailedId) {
                    const failedPair = this.selectedPairs.find(p => p.existingCvId === response.lockFailedId);
                    if (failedPair) {
                        alertText = `「${this.pairListLabel(failedPair)}」: ${alertText}`;
                    }
                }
                window.alert(this.alertMessage + alertText);
                this.$toasted.error('削除に失敗しました。')
                return;
            }
            this.$store.dispatch('doUpdateIsInputChanged', false);
            this.$emit('update:isRefresh', true);
            this.closeModal();
            this.$toasted.success('削除しました。');
        }).catch(() => {
            this.$toasted.error('削除に失敗しました。');
        }).finally(() => {
            this.isLoading = false;
        });
    }

    cancelForm() {
        if (!this.$store.getters.isInputChanged) return;
        if (!window.confirm('変更内容を破棄します。よろしいですか？')) return false;
        this.$store.dispatch('doUpdateIsInputChanged', false);
        this.handlePopupOpening();
    }

    formatDateTime(datetime: string): string {
        return this.$apiClient.formatJst(datetime);
    }

    formatDate(date: Date | string | null): string {
        if (!date) return '〇';
        const dt = new Date(date);
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${dt.getFullYear()}/${pad(dt.getMonth() + 1)}/${pad(dt.getDate())}`;
    }

    formatKisyaku(usage: ChemicalWorkUsage): string {
        if (!usage || usage.dilRatio == null || !usage.dilRatioUnit) return '---';
        return `${this.formatNumberWithCommas(Number(usage.dilRatio))} ${this.truncateText(usage.dilRatioUnit, 6)}`;
    }

    formatSanpu(usage: ChemicalWorkUsage, areaOverride?: number | null): string {
        const area = areaOverride !== undefined ? areaOverride : this.form.agriFieldUsageArea;
        if (!usage || usage.sprayQty == null || !usage.sprayUnit || !area) {
            return '---';
        }
        const { sprayQty: qty, sprayUnit: unit, unitOfMeasure: measure } = usage;
        if (!['mL', 'L', 'cc'].includes(unit)) {
            return `${this.formatNumberWithCommas(Number(qty))} ${this.truncateText(unit, 6)}`;
        }
        return this.isPer10aCheckedChemical
        ? `${this.formatNumberWithCommas(measure === 1 ? qty : (qty / area * 10))} ${unit}/10a`
        : `${this.formatNumberWithCommas(measure === 1 ? (qty * area / 10 ): qty)} ${unit}${ this.isPer10aCheckedChemical ?'/10a' : ''}`;
    }

    formatIngredientFor(chemicals: CvhChemical[]): Record<number, string> {
        const sorted = this.sortedChemicals(chemicals);
        const usageCounter: Record<string, number> = {};
        const limitMap: Record<string, number> = {};
        const exceeded = new Set<string>();
        sorted.forEach((chem: any) => {
            chem.workUsages?.forEach((usage: any) => {
                usage.ingSelConditions?.forEach((cond: any) => {
                    if (cond.usageCount == null) return;
                    const key = `${cond.name}|${chem.disiDiv}|${chem.usageCountOrigin ?? 'NOTSET'}|${cond.usageCountOrigin ?? 'NOTSET'}`;
                    const pointValue = chem.ingredients?.find((i: any) => i.name === cond.name)?.pointValue ?? 0;
                    usageCounter[key] = (usageCounter[key] ?? 0) + (pointValue > 0 ? 1 : 0);
                    if (limitMap[key] == null) {
                        limitMap[key] = cond.usageCount;
                    }
                });
            });
        });
        Object.keys(usageCounter).forEach(key => {
            if (limitMap[key] != null && usageCounter[key] > limitMap[key]) {
                exceeded.add(key);
            }
        });
        return sorted.reduce((result: Record<number, string>, chem: any, index: number) => {
            const exceededNames = new Set<string>();
            chem.workUsages?.forEach((usage: any) => {
                usage.ingSelConditions?.forEach((cond: any) => {
                    const key = `${cond.name}|${chem.disiDiv}|${chem.usageCountOrigin ?? 'NOTSET'}|${cond.usageCountOrigin ?? 'NOTSET'}`;
                    if (exceeded.has(key)) {
                        exceededNames.add(cond.name);
                    }
                });
            });
            const text = chem.ingredients?.map((i: any) => {
                const expired = i.isExpired ? '<span class="red-label">（失効）</span>' : '';
                const exceededLabel = exceededNames.has(i.name) ? '<br><span class="red-label">成分使用回数上限値を超えました。</span>' : '';
                return `${i.name ?? "---"}${expired}${exceededLabel}`;
            }).join('<br>') || '---';
            result[index] = text;
            return result;
        }, {});
    }

    get bulkFormatIngredient(): Record<number, string> {
        return this.formatIngredientFor(this.bulkChemicals);
    }

    get individualFormatIngredient(): Record<number, string> {
        return this.formatIngredientFor(this.mergedIndividualChemicals);
    }

    get totalApplicationsInfo(): string {
        const chemicals = this.currentDisplayChemicals;
        if (!chemicals.length) return "---";
        let total = 0;
        const ingredientCounts = new Map<string, number>();
        for (const chem of chemicals) {
            const usageCount = chem.workUsages?.length ?? 0;
            if (usageCount === 0) continue;
            const totalPointValue = (chem.ingredients ?? []).reduce((sum, ing) => sum + ing.pointValue, 0);
            total += usageCount * totalPointValue;
            for (const ing of chem.ingredients ?? []) {
                ingredientCounts.set(ing.name, (ingredientCounts.get(ing.name) ?? 0) + usageCount);
            }
        }
        const limit = this.sitem?.chemicalUseCount ?? '-';
        this.ingredientStr = ingredientCounts.size
            ? ` 成分合計（${[...ingredientCounts].map(([n, c]) => `${n}：${c}回`).join('、')}）`
            : '';

        return `点数合計：${total}回（${limit}回まで）`;
    }

    get nouyakuTableTotal(): number {
        const chemicals = this.currentDisplayChemicals;
        return chemicals.reduce((sum, chem) => {
            const usageCount = chem.workUsages?.length ?? 0;
            if (usageCount === 0) return sum;
            const totalPointValue = (chem.ingredients ?? []).reduce((sum, ing) => sum + ing.pointValue, 0);
            return sum + (usageCount * totalPointValue);
        }, 0);
    }

    get sehiTableAlert(): string {
        if (this.selectedPairs.length >= 2 && this.fertTabPairSelection === 'bulk') return '';
        const { sehiTableTotal: t, sitem } = this;
        return !t ? '' :
            sitem?.kcontTotal != null && t > sitem.kcontTotal ? '※上限値を超えました。' :
            sitem?.kcontTotalAlert != null && t >= sitem.kcontTotalAlert ? '※上限値に近づいています。' : '';
    }

    get nouyakuTableAlert(): string {
        const { nouyakuTableTotal: t, sitem } = this;
        return !t ? '' :
            sitem?.chemicalUseCount != null && t > sitem.chemicalUseCount ? '※農薬使用点数の上限値を超えました。' :
            sitem?.chemicalUseCountAlert != null && t >= sitem.chemicalUseCountAlert ? '※農薬使用点数の上限値に近づいています。' : '';
    }

    maxUsageAlertsFor(chemicals: CvhChemical[]): Record<number, string> {
        const sorted = this.sortedChemicals(chemicals);
        const { usageCounter, limitMap } = sorted.reduce((acc, chem) => {
            const key = `${chem.chemicalId}|${chem.disiDiv}|${chem.usageCountOrigin ?? "NOTSET"}`;
            const workUsageCount = chem.workUsages?.length ?? 0;
            const totalPointValue = (chem.ingredients ?? []).reduce((sum: number, ing: Ingredient) => sum + ing.pointValue, 0);
            const usage = workUsageCount *  (totalPointValue > 0 ? 1 : 0);
            acc.usageCounter[key] = (acc.usageCounter[key] ?? 0) + usage;
            if (chem.usageCount != null && acc.limitMap[key] == null) {
                acc.limitMap[key] = chem.usageCount;
            }
            return acc;
        }, {
            usageCounter: {} as Record<string, number>,
            limitMap: {} as Record<string, number>
        });

        const exceeded = new Set(Object.keys(usageCounter).filter(k => limitMap[k] != null && usageCounter[k] > limitMap[k]));
        return sorted.reduce((alerts, chem, index) => {
            const key = `${chem.chemicalId}|${chem.disiDiv}|${chem.usageCountOrigin}`;
            alerts[index] = exceeded.has(key) ? '農薬使用回数上限値を超えました。' : '';
            return alerts;
        }, {} as Record<number, string>);
    }

    get bulkMaxUsageAlerts(): Record<number, string> {
        return this.maxUsageAlertsFor(this.bulkChemicals);
    }

    get individualMaxUsageAlerts(): Record<number, string> {
        return this.maxUsageAlertsFor(this.mergedIndividualChemicals);
    }

    chemicalUsageStatsFor(chemicals: CvhChemical[]): { usageCounter: Record<string, number>; limitMap: Record<string, number>; exceeded: Set<string> } {
        const list = chemicals ?? [];
        const usageCounter: Record<string, number> = {};
        const limitMap: Record<string, number> = {};

        list.forEach((chem) => {
            const key = `${chem.chemicalId}|${chem.disiDiv}|${chem.usageCountOrigin ?? "NOTSET"}`;
            const workUsageCount = chem.workUsages?.length ?? 0;
            const totalPointValue = (chem.ingredients ?? []).reduce((sum: number, ing: Ingredient) => sum + (ing.pointValue ?? 0), 0);
            const usage = workUsageCount * (totalPointValue > 0 ? 1 : 0)
            usageCounter[key] = (usageCounter[key] ?? 0) + usage;
            if (chem.usageCount != null && limitMap[key] == null) {
                limitMap[key] = chem.usageCount;
            }
        });

        const exceeded = new Set<string>();
        Object.keys(usageCounter).forEach((key) => {
            if (limitMap[key] != null && usageCounter[key] > limitMap[key]) {
                exceeded.add(key);
            }
        });
        return { usageCounter, limitMap, exceeded };
    }

    ingredientUsageStatsFor(chemicals: CvhChemical[]): { usageCounter: Record<string, number>; limitMap: Record<string, number>; exceeded: Set<string> } {
        const list = chemicals ?? [];
        const usageCounter: Record<string, number> = {};
        const limitMap: Record<string, number> = {};

        list.forEach((chem) => {
            (chem.workUsages || []).forEach((usage) => {
                (usage.ingSelConditions || []).forEach((cond) => {
                    if (cond.usageCount == null) return;
                    const key = `${cond.name}|${chem.disiDiv}|${chem.usageCountOrigin ?? 'NOTSET'}|${cond.usageCountOrigin ?? 'NOTSET'}`;
                    const pointValue = chem.ingredients?.find((i) => i.name === cond.name)?.pointValue ?? 0;
                    usageCounter[key] = (usageCounter[key] ?? 0) + (pointValue > 0 ? 1 : 0);
                    if (limitMap[key] == null) {
                        limitMap[key] = cond.usageCount;
                    }
                });
            });
        });

        const exceeded = new Set<string>();
        Object.keys(usageCounter).forEach((key) => {
            if (limitMap[key] != null && usageCounter[key] > limitMap[key]) {
                exceeded.add(key);
            }
        });

        return { usageCounter, limitMap, exceeded };
    }

    handleSelectChange() {
        const params = {
            screenName: "CULTIVATIONHISTORY-CHEMICAL",
            showChemCols: this.showChemCols
        }
        this.$apiClient.doRequest('post', '/graphScreenManagement', params)
        .catch(() => {
            this.$toasted.error('データの取得に失敗しました。')
        })
    }

    validateTenantSelection() {
        const selected = this.tenants.find(t => t.id === this.form.pdTenantId);
        this.isTenantDisabled = selected?.deleted === true;
    }

    getSortedUsages(data: any) {
        return [...(data?.workUsages || [])].sort((a, b) => new Date(a.workDate).getTime() - new Date(b.workDate).getTime());
    }

    sortedChemicals(chemicals: any) {
        if (!Array.isArray(chemicals)) return [];

        return [...chemicals].sort((a, b) => {
            const name = (a.chemicalName || "").localeCompare(b.chemicalName || "");
            if (name) return name;

            const wa = a.warehouseName !== "未管理" ? a.warehouseName || "" : "";
            const wb = b.warehouseName !== "未管理" ? b.warehouseName || "" : "";
            const warehouse = wa && wb ? wa.localeCompare(wb) : wa ? 1 : wb ? -1 : 0;
            if (warehouse) return warehouse;

            return this.getDisinfectionLabel(a.disiDiv).localeCompare(this.getDisinfectionLabel(b.disiDiv));
        });
    }

    sortedFertilizers(fertilizers: any[]) {
        if (!Array.isArray(fertilizers)) return [];

        return [...fertilizers].sort((a, b) => {
            const name = (a.fertilizerName ?? "").localeCompare(b.fertilizerName ?? "");
            if (name) return name;

            const wa = a.warehouseName !== "未管理" ? a.warehouseName ?? "" : "";
            const wb = b.warehouseName !== "未管理" ? b.warehouseName ?? "" : "";

            return wa && wb ? wa.localeCompare(wb) : wa ? 1 : wb ? -1 : 0;
        });
    }

    isGreyCell(cvhc: CvhChemical, usage: ChemicalWorkUsage | null, additionalIndex: number | null, chemicals?: CvhChemical[]) {
        if (additionalIndex == null) return false;

        const { usageCounter: chemUsageCounter, limitMap: chemLimitMap } = this.chemicalUsageStatsFor(chemicals ?? []);
        const { usageCounter: ingUsageCounter, limitMap: ingLimitMap } = this.ingredientUsageStatsFor(chemicals ?? []);

        const chemKey = `${cvhc.chemicalId}|${cvhc.disiDiv}|${cvhc.usageCountOrigin ?? "NOTSET"}`;
        const chemLimit = chemLimitMap[chemKey];
        const chemUsed = chemUsageCounter[chemKey] ?? 0;
        const remainingChem = chemLimit == null ? Infinity : chemLimit - chemUsed;

        let remainingIng = Infinity;
        const conditions = cvhc.workUsages?.[0]?.ingSelConditions || [];
        conditions.forEach((cond) => {
            if (cond.usageCount == null) return;
            const key = `${cond.name}|${cvhc.disiDiv}|${cvhc.usageCountOrigin ?? 'NOTSET'}|${cond.usageCountOrigin ?? 'NOTSET'}`;
            const limit = ingLimitMap[key];
            if (limit == null) return;
            const used = ingUsageCounter[key] ?? 0;
            remainingIng = Math.min(remainingIng, limit - used);
        });

        const remaining = Math.min(remainingChem, remainingIng);
        const available = Math.max(0, remaining);
        return additionalIndex > available;
    }

    isRedCell(cvhc: CvhChemical, usage: ChemicalWorkUsage | null, additionalIndex: number | null, chemicals?: CvhChemical[]) {
        if (!usage) return false;

        const { exceeded: chemExceeded } = this.chemicalUsageStatsFor(chemicals ?? []);
        const { exceeded: ingExceeded } = this.ingredientUsageStatsFor(chemicals ?? []);

        const chemKey = `${cvhc.chemicalId}|${cvhc.disiDiv}|${cvhc.usageCountOrigin ?? "NOTSET"}`;
        if (chemExceeded.has(chemKey)) return true;

        const conditions = (usage.ingSelConditions && usage.ingSelConditions.length)
            ? usage.ingSelConditions
            : (cvhc.workUsages?.[0]?.ingSelConditions || []);

        return conditions.some((cond) => {
            const key = `${cond.name}|${cvhc.disiDiv}|${cvhc.usageCountOrigin ?? 'NOTSET'}|${cond.usageCountOrigin ?? 'NOTSET'}`;
            return ingExceeded.has(key);
        });
    }

    getFertilizerUnits() {
        return this.$apiClient.doRequest('get','/unit_conversion/fertilizer').then((res) => {
            this.fertilizerUnits = res.weight_units
        })
    }

    getChemicalUnits() {
        return this.$apiClient.doRequest('get','/unit_conversion/chemicals').then((res) => {
            this.chemicalUnits = res.weight_units || [];
        })
    }

    getWarehouseChemicals(): Promise<void> {
        if (!this.form.pdTenantId) return Promise.resolve();
        return this.$apiClient.doRequest('get', `/admin/warehouses/warehouse_chemicals/${this.form.pdTenantId}`).then((res) => {
            this.warehouseChemicals = res.warehouseChemicals || [];
        });
    }

    getWarehouseFertilizers() : Promise<void>{
        if (!this.form.pdTenantId) return Promise.resolve();
        return this.$apiClient.doRequest('get', `/admin/warehouses/warehouse_fertilizers/${this.form.pdTenantId}?sp_tenant_id=${this.form.tenantId || ''}`).then((res) => {
            this.warehouseFertilizers = res.warehouseFertilizers || [];
        })
    }

    filteredChemicalUnits(chemicalId: number | null, warehouseId: string | null): any[] {
        if (!chemicalId) return this.chemicalUnits;
        const wc = this.warehouseChemicals.find(w => w.chemicalId === chemicalId && (w.warehouseId === warehouseId || w.warehouseName === '未管理'));
        if (!wc) return this.chemicalUnits;
        const isWeightNull = wc.weightPerUnit == null;
        return this.chemicalUnits.filter(unit => unit.name === wc.unitName && (isWeightNull ? unit.weight_per_unit == null : unit.weight_per_unit != null));
    }

    filteredFertilizerUnits(fertilizerId: string | null, warehouseId: string | null): any[] {
        if (!fertilizerId) return this.fertilizerUnits;
        const wc = this.warehouseFertilizers.find(w => (w.fertilizerId === fertilizerId || w.resolvedFertilizerId === fertilizerId) && (w.warehouseId === warehouseId || w.warehouseName === '未管理'));
        if (!wc) return this.fertilizerUnits;
        const isWeightNull = wc.weightPerUnit == null;
        return this.fertilizerUnits.filter(unit => unit.name === wc.unitName && (isWeightNull ? unit.weight_per_unit == null : unit.weight_per_unit != null));
    }

    getDisinfectionLabel(value?: number | null): string {
        const labels: Record<number, string> = {
            3: '種子',
            1: '育苗中',
            2: '本圃'
        };
        return labels[value ?? -1] ?? '未選択';
    }

    hasUsageAlert(cvhc: CvhChemical, usage: ChemicalWorkUsage | null): boolean {
        if (cvhc.cropOutOfScope) return true;
        if (cvhc.conditionsNotSelected) return true;
        if (!usage) return false;
        const { dilRatioMin, dilRatioMax, sprayQtyMin, sprayQtyMax } = cvhc;
        if (usage.dilRatio != null && dilRatioMin != null && dilRatioMax != null &&
            (usage.dilRatio < dilRatioMin || usage.dilRatio > dilRatioMax)) {
            return true;
        }
        if (usage.sprayQty != null && sprayQtyMin != null && sprayQtyMax != null &&
            (usage.sprayQty < sprayQtyMin || usage.sprayQty > sprayQtyMax)) {
            return true;
        }
        return false;
    }

    get preSelectPairsForModal(): SelectedPlantingAgriFieldPair[] {
        const realPairs = this.selectedPairs.filter(p => p.plantingId && p.agriFieldId);
        if (realPairs.length > 0) {
            return realPairs.map(p => ({
                planting: { id: p.plantingId ?? '', name: p.plantingName ?? '', cropFamicId: p.cropFamicId ?? '' },
                agriField: { id: p.agriFieldId ?? '', name: p.agriFieldName ?? '', plantedArea: p.agriFieldUsageArea ?? 0 }
            }));
        }
        if (this.form.plantingId && this.form.agriFieldId) {
            return [{
                planting: { id: this.form.plantingId, name: this.form.plantingName ?? '', cropFamicId: this.cropFamicId ?? '' },
                agriField: { id: this.form.agriFieldId, name: '', plantedArea: this.form.agriFieldUsageArea ?? 0 }
            }];
        }
        return [];
    }

    private createEmptyPairBasicData(pair: SelectedPlantingAgriFieldPair): PairBasicData {
        return {
            existingCvId: null,
            updatedLocal: null,
            tempPlantingKey: null,
            plantingId: pair.planting.id,
            plantingName: pair.planting.name,
            plantingStartDate: null,
            plantingEndDate: null,
            seedlingId: null,
            seedlingQty: null,
            seedlingUnitId: null,
            agriFieldId: pair.agriField.id,
            agriFieldName: pair.agriField.name,
            agriFieldNum: null,
            agriFieldBranchNum: null,
            agriFieldUsageArea: null,
            totalAgriFieldUsageArea: this.agriFields.find(af => af.id === pair.agriField.id)?.plantedArea ?? null,
            remainingAgriFieldUsageArea: null,
            prevCrops: null,
            cropFamicId: pair.planting.cropFamicId,
            cropName: null,
            workRecords: [],
            fertilizers: [],
            chemicals: [],
            seedSd: null,
            checkPeriod: null,
            cropType: null,
            purTrns: null,
            totalYield: null,
            totalShpVol: null,
            created: null,
            createdUserName: null,
            updated: null,
            updatedUserName: null,
        };
    }

    private blankPairBasicData(): PairBasicData {
        return {
            existingCvId: null,
            updatedLocal: null,
            tempPlantingKey: `new::${Date.now()}::${Math.random().toString(36).slice(2)}`,
            plantingId: null,
            plantingName: null,
            plantingStartDate: null,
            plantingEndDate: null,
            seedlingId: null,
            seedlingQty: null,
            seedlingUnitId: null,
            agriFieldId: null,
            agriFieldName: null,
            agriFieldNum: null,
            agriFieldBranchNum: null,
            agriFieldUsageArea: null,
            totalAgriFieldUsageArea: null,
            remainingAgriFieldUsageArea: null,
            prevCrops: null,
            cropFamicId: null,
            cropName: null,
            workRecords: [],
            fertilizers: [],
            chemicals: [],
            seedSd: null,
            checkPeriod: null,
            cropType: null,
            purTrns: null,
            totalYield: null,
            totalShpVol: null,
            created: null,
            createdUserName: null,
            updated: null,
            updatedUserName: null,
        };
    }

    private async fetchPairDetail(plantingId: string, agriFieldId: string): Promise<Partial<PairBasicData>> {
        const detail: Partial<PairBasicData> = {};
        try {
            const res = await this.$apiClient.doRequest('get', `/plantings/${plantingId}`);
            detail.seedlingId = res.seedling?.id || null;
            detail.seedlingQty = res.seedling?.seedlings_quantity || null;
            detail.seedlingUnitId = res.seedling?.seedling_unit_id || null;
            detail.plantingStartDate = res.work_start_date;
            detail.plantingEndDate = res.work_end_date;
            detail.cropFamicId = res.cropFamicId || null;
            detail.cropName = res.crop?.name || null;
            detail.agriFieldUsageArea = res.agri_fields?.find((af: { id: string | null }) => af.id === agriFieldId)?.using_area ?? null;
        } catch {
            this.$toasted.error('データの取得に失敗しました。');
        }
        try {
            const prevRes = await this.$apiClient.doRequest('get', `/agri_fields/${agriFieldId}/previous_crops?planting_id=${plantingId}`);
            detail.prevCrops = prevRes.prevCrops || null;
        } catch {
            this.$toasted.error('データの取得に失敗しました。');
        }
        return detail;
    }

    private applyPairToForm(pair: PairBasicData) {
        this.form.plantingId = pair.plantingId;
        this.form.plantingName = pair.plantingName;
        this.form.plantingStartDate = pair.plantingStartDate;
        this.form.plantingEndDate = pair.plantingEndDate;
        this.form.seedlingId = pair.seedlingId;
        this.form.seedlingQty = pair.seedlingQty;
        this.form.seedlingUnitId = pair.seedlingUnitId;
        this.form.agriFieldId = pair.agriFieldId;
        this.form.agriFieldNum = pair.agriFieldNum;
        this.form.agriFieldBranchNum = pair.agriFieldBranchNum;
        this.form.agriFieldUsageArea = pair.agriFieldUsageArea;
        this.form.totalAgriFieldUsageArea = pair.totalAgriFieldUsageArea;
        this.form.remainingAgriFieldUsageArea = pair.remainingAgriFieldUsageArea;
        this.form.prevCrops = pair.prevCrops;
        this.form.seedSd = pair.seedSd;
        this.form.checkPeriod = pair.checkPeriod;
        this.form.cropType = pair.cropType;
        this.form.purTrns = pair.purTrns;
        this.form.totalYield = pair.totalYield;
        this.form.totalShpVol = pair.totalShpVol;
        this.form.updatedLocal = pair.updatedLocal;
        this.form.created = pair.created;
        this.form.createdUserName = pair.createdUserName;
        this.form.updated = pair.updated;
        this.form.updatedUserName = pair.updatedUserName;
        this.cropFamicId = pair.cropFamicId;
        this.cropName = pair.cropName;
        this.previousSeedlingId = pair.seedlingId;
    }

    private syncFormToActivePair() {
        const pair = this.selectedPairs[this.activePairIndex];
        if (!pair) return;
        pair.plantingId = this.form.plantingId;
        pair.plantingName = this.form.plantingName;
        pair.plantingStartDate = this.form.plantingStartDate ?? null;
        pair.plantingEndDate = this.form.plantingEndDate ?? null;
        pair.seedlingId = this.form.seedlingId;
        pair.seedlingQty = this.form.seedlingQty;
        pair.seedlingUnitId = this.form.seedlingUnitId;
        pair.agriFieldId = this.form.agriFieldId;
        pair.agriFieldName = this.agriFields.find(af => af.id === this.form.agriFieldId)?.name ?? null;
        pair.agriFieldNum = this.form.agriFieldNum;
        pair.agriFieldBranchNum = this.form.agriFieldBranchNum;
        pair.agriFieldUsageArea = this.form.agriFieldUsageArea;
        pair.totalAgriFieldUsageArea = this.form.totalAgriFieldUsageArea;
        pair.remainingAgriFieldUsageArea = this.form.remainingAgriFieldUsageArea ?? null;
        pair.prevCrops = this.form.prevCrops;
        pair.seedSd = this.form.seedSd;
        pair.checkPeriod = this.form.checkPeriod;
        pair.cropType = this.form.cropType;
        pair.purTrns = this.form.purTrns;
        pair.totalYield = this.form.totalYield;
        pair.totalShpVol = this.form.totalShpVol;
        pair.cropFamicId = this.cropFamicId;
        pair.cropName = this.cropName;
    }

    async commitSelectedPairs(pairs: SelectedPlantingAgriFieldPair[]) {
        if (pairs.length === 0) return;

        const beforeKeys = new Set(
            this.selectedPairs
                .filter(p => p.plantingId && p.agriFieldId)
                .map(p => `${p.plantingId}_${p.agriFieldId}`)
        );
        const afterKeys = new Set(pairs.map(p => `${p.planting.id}_${p.agriField.id}`));
        const selectionChanged = beforeKeys.size !== afterKeys.size || [...afterKeys].some(k => !beforeKeys.has(k));
        if (!selectionChanged) return;

        if (!window.confirm('保存していないデータは破棄されますが、よろしいですか？')) return;

        this.syncFormToActivePair();

        const originalPair = this.editingOriginalPairKey
            ? this.selectedPairs.find(p => `${p.plantingId}_${p.agriFieldId}` === this.editingOriginalPairKey)
            : undefined;
        const originalUpdatedLocal = originalPair?.updatedLocal ?? this.form.updatedLocal ?? null;

        this.resetPlanting();

        this.isLoading = true;
        const newPairs: PairBasicData[] = [];
        for (const pair of pairs) {
            const base = this.createEmptyPairBasicData(pair);
            const detail = await this.fetchPairDetail(pair.planting.id, pair.agriField.id);
            newPairs.push({ ...base, ...detail });
        }
        this.isLoading = false;

        if (this.mode === 'edit' && this.tempCultivationId && this.editingOriginalPairKey) {
            const matchIndex = newPairs.findIndex(p => `${p.plantingId}_${p.agriFieldId}` === this.editingOriginalPairKey);
            if (matchIndex !== -1) {
                newPairs[matchIndex].existingCvId = this.tempCultivationId;
                newPairs[matchIndex].updatedLocal = originalUpdatedLocal;
                newPairs[matchIndex].created = originalPair?.created ?? null;
                newPairs[matchIndex].createdUserName = originalPair?.createdUserName ?? null;
                newPairs[matchIndex].updated = originalPair?.updated ?? null;
                newPairs[matchIndex].updatedUserName = originalPair?.updatedUserName ?? null;
            }
        }

        const blankPairs = this.selectedPairs.filter(p => !p.plantingId);
        this.selectedPairs = [...newPairs, ...blankPairs];
        this.activePairIndex = 0;
        this.applyPairToForm(this.selectedPairs[0]);
        await this.autoLinkExistingPairs();
        this.inputChanged();
        await this.syncGroupForPairChange();
    }

    selectPair(index: number) {
        if (index === this.activePairIndex || !this.selectedPairs[index]) return;
        this.syncFormToActivePair();
        this.activePairIndex = index;
        this.applyPairToForm(this.selectedPairs[index]);
    }

    async addBlankPair() {
        if (!this.form.pdTenantId) {
            window.alert('生産者の対象テナントを選択してから、作付を追加してください。');
            return;
        }
        if (!this.form.sitemId) {
            window.alert('出荷品目を選択してから、作付を追加してください。');
            return;
        }
        if (!this.confirmPairChange()) return;
        this.syncFormToActivePair();
        this.selectedPairs.unshift(this.blankPairBasicData());
        this.activePairIndex = 0;
        this.resetPlanting();
        this.inputChanged();
        await this.syncGroupForPairChange();
    }

    async removePair(index: number) {
        const target = this.selectedPairs[index];
        if (!target) return;
        if (!this.confirmPairChange()) return;
        this.syncFormToActivePair();
        const removingActive = index === this.activePairIndex;
        this.selectedPairs.splice(index, 1);

        if (this.selectedPairs.length === 0) {
            this.activePairIndex = 0;
            this.resetPlanting();
            this.inputChanged();
            await this.syncGroupForPairChange();
            return;
        }

        if (removingActive) {
            this.activePairIndex = Math.min(index, this.selectedPairs.length - 1);
            this.applyPairToForm(this.selectedPairs[this.activePairIndex]);
        } else if (index < this.activePairIndex) {
            this.activePairIndex -= 1;
        }
        this.inputChanged();
        await this.syncGroupForPairChange();
    }

    private validatePairData(pair: PairBasicData): string[] {
        const errors: string[] = [];
        const regex = /^\d{1,7}(\.\d{1,2})?$/;
        pair.cropType = pair.cropType?.trim() || null;
        pair.purTrns = pair.purTrns?.trim() || null;
        pair.plantingName = pair.plantingName?.trim() || null;

        if (!pair.plantingId && !pair.plantingName) {
            errors.push('作付名を選択してください。');
        }
        if (!pair.plantingId && pair.plantingName && pair.plantingName.length > 255) {
            errors.push(`作付名は255文字以内で入力してください。（現在は${pair.plantingName.length}文字）`);
        }
        if (pair.plantingName && (!pair.plantingStartDate || !pair.plantingEndDate)) {
            errors.push('栽培期間を入力してください。');
        } else if (pair.plantingName && pair.plantingStartDate && pair.plantingEndDate && pair.plantingEndDate <= pair.plantingStartDate) {
            errors.push('栽培期間の終了日は開始日より後にしてください。');
        }
        if (!pair.plantingId && pair.seedlingId) {
            const seedlingQtyStr = pair.seedlingQty != null ? pair.seedlingQty.toString().trim() : '';
            const isSeedlingQtyFilled = seedlingQtyStr !== '';
            const isUnitFilled = pair.seedlingUnitId != null && pair.seedlingUnitId !== '';
            if ((isSeedlingQtyFilled && !isUnitFilled) || (!isSeedlingQtyFilled && isUnitFilled)) {
                errors.push('種子量と単位は両方入力してください。');
            }
            if (isSeedlingQtyFilled && !regex.test(seedlingQtyStr)) {
                errors.push('種子量は整数部7桁、小数部2桁までの数値を入力してください。');
            }
        }
        if (!pair.agriFieldId) {
            errors.push('圃場名を選択してください。');
        }
        if (pair.agriFieldId) {
            const agriFieldUsageArea = pair.agriFieldUsageArea?.toString() ?? null;
            if (agriFieldUsageArea === null) {
                errors.push('面積を入力してください。');
            } else if (!regex.test(agriFieldUsageArea) || parseFloat(agriFieldUsageArea) <= 0) {
                errors.push('面積は0より大きい整数部7桁、小数部2桁までの数値を入力してください。');
            } else if (!pair.plantingId && pair.remainingAgriFieldUsageArea != null && parseFloat(agriFieldUsageArea) > pair.remainingAgriFieldUsageArea) {
                errors.push(`面積が圃場の空き面積（${pair.remainingAgriFieldUsageArea.toLocaleString('ja-JP')}a）を超えています。利用する圃場を変更するか、面積を変更してください。`);
            }
        }
        if (pair.cropType && pair.cropType.length > 40) {
            errors.push(`作型は40文字以内で入力してください。（現在は${pair.cropType.length}文字）`);
        }
        if (pair.purTrns && pair.purTrns.length > 200) {
            errors.push(`購入/譲受先は200文字以内で入力してください。（現在は${pair.purTrns.length}文字）`);
        }
        const totalYield = pair.totalYield?.toString() ?? null;
        if (totalYield !== null && totalYield !== '' && !regex.test(totalYield)) {
            errors.push('総収穫量は整数部7桁、小数部2桁までの数値を入力してください。');
        }
        const totalShpVol = pair.totalShpVol?.toString() ?? null;
        if (totalShpVol !== null && totalShpVol !== '' && !regex.test(totalShpVol)) {
            errors.push('総出荷量は整数部7桁、小数部2桁までの数値を入力してください。');
        }
        return errors;
    }

    private buildPairPayload(pair: PairBasicData) {
        return {
            id: pair.existingCvId ?? undefined,
            groupId: this.tempGroupId ?? undefined,
            updatedLocal: pair.existingCvId ? pair.updatedLocal : undefined,
            tenantId: this.form.tenantId,
            comId: this.form.comId,
            pdId: this.form.pdId,
            pdTenantId: this.form.pdTenantId,
            sitemId: this.form.sitemId,
            plantingId: pair.plantingId,
            tempPlantingKey: pair.plantingId ? undefined : (pair.tempPlantingKey ?? undefined),
            plantingName: pair.plantingName,
            plantingStartDate: pair.plantingStartDate,
            plantingEndDate: pair.plantingEndDate,
            seedlingId: pair.seedlingId,
            seedlingQty: pair.seedlingQty,
            seedlingUnitId: pair.seedlingUnitId,
            agriFieldId: pair.agriFieldId,
            agriFieldUsageArea: pair.agriFieldUsageArea,
            seedSd: pair.seedSd,
            checkPeriod: pair.checkPeriod,
            cropType: pair.cropType,
            purTrns: pair.purTrns,
            totalYield: pair.totalYield,
            totalShpVol: pair.totalShpVol,
            workRecords: [],
            deletedWorkRecordIds: [],
            fertilizers: [],
            deletedWorkRecordFertilizerIds: [],
            deletedHistoryFertilizerIds: [],
            chemicals: [],
            deletedWorkRecordChemicalIds: [],
            deletedHistoryChemicalIds: [],
        };
    }

    private buildWorkRecordBatchesPayload() {
        const batches = this.bulkWorkRecords
            .filter(b => b.processType === 'ADD' || b.processType === 'UPDATE')
            .map(b => ({
                id: b.processType === 'UPDATE' ? b.id : undefined,
                clientTempId: b.processType === 'ADD' ? b.id : undefined,
                workItemId: b.workItemId,
                workItemName: b.workItemName,
                workDate: b.workDate,
                processType: b.processType,
            }));
        if (batches.length === 0 && this.deletedWorkRecordBatchIds.length === 0) {
            return undefined;
        }
        return {
            batches,
            deletedBatchIds: this.deletedWorkRecordBatchIds,
        };
    }

    private validateBasicTabData(): string {
        const errors: string[] = [];
        if (!this.form.tenantId) errors.push('集出荷団体を選択してください。');
        if (!this.form.comId) errors.push('部会を選択してください。');
        if (!this.form.pdId) errors.push('氏名を選択してください。');
        const selectedCommittee = this.committees.find(c => c.id === this.form.comId);
        const selectedProducer = selectedCommittee?.producers.find(p => p.pdId === this.form.pdId);
        if (selectedProducer?.pdNo == null) errors.push('生産者番号が登録されていません。');
        if (!this.form.pdTenantId) errors.push('テナントを選択してください。');
        if (!this.form.sitemId) errors.push('出荷品目を選択してください。');
        if (this.selectedPairs.length === 0) errors.push('作付・圃場を追加してください。');

        this.syncFormToActivePair();
        const pairErrorGroups: string[] = [];
        this.selectedPairs.forEach((pair, index) => {
            const pairErrors = this.validatePairData(pair);
            if (pairErrors.length === 0) return;
            const label = `${index + 1}件目 ${this.pairListLabel(pair)}`;
            const numbered = pairErrors.map((msg, i) => `    ${i + 1}. ${msg}`).join('\n');
            pairErrorGroups.push(`${label}:\n${numbered}`);
        });

        return [errors.join('\n'), pairErrorGroups.join('\n\n')].filter(Boolean).join('\n\n');
    }

    async saveFormGroup() {
        const basicTabMessage = this.validateBasicTabData();

        const workErrorGroups: string[] = [];
        const workRows = this.bulkWorkRecords.map(batch => ({ workDate: batch.workDate, workItemId: batch.workItemId, workItemName: batch.workItemName }));
        workRows.forEach((record, index) => {
            const rowErrors: string[] = [];
            const workItemName = record.workItemName?.trim() || null;
            const hasWorkItem = record.workItemId != null || workItemName !== null;
            if (!record.workDate) {
                rowErrors.push('日付を入力してください。');
            }
            if (!hasWorkItem) {
                rowErrors.push('作業項目を選択または入力してください。');
            }
            if (workItemName && workItemName.length > 100) {
                rowErrors.push(`作業項目名は100文字以内で入力してください。（現在は${workItemName.length}文字）`);
            }
            if (rowErrors.length === 0) return;
            const label = `作業内容 ${index + 1}件目`;
            const numbered = rowErrors.map((msg, i) => `    ${i + 1}. ${msg}`).join('\n');
            workErrorGroups.push(`${label}:\n${numbered}`);
        });

        const usageRegex = /^\d{1,7}(\.\d{1,2})?$/;
        const amountRegex = /^\d{1,10}$/;
        const fertChemErrorGroups: string[] = [];
        const workRecordDateMapFert = new Map<string, { date: string; rowInfo: string }>();
        this.bulkFertilizers.forEach((cvhf) => {
            const fertLabel = `${cvhf.fertilizerName}${cvhf.warehouseName && cvhf.warehouseName !== '未管理' ? `（${cvhf.warehouseName}）` : ''}`;
            const seenDates = new Set<string>();
            cvhf.workUsages.forEach((usage, uIndex) => {
                if (cvhf.warehouseId && !usage.workDate) {
                    fertChemErrorGroups.push(`「${fertLabel}」の${uIndex + 1}番目の使用日を入力してください。`);
                }
                if (usage.workDate) {
                    const dateStr = usage.workDate.toString();
                    if (seenDates.has(dateStr)) {
                        fertChemErrorGroups.push(`「${fertLabel}」の${uIndex + 1}番目の使用日は、同じ肥料で既に使用されています。別の日付を入力してください。`);
                    } else {
                        seenDates.add(dateStr);
                    }
                }
                if (usage.workRecordId && usage.workDate) {
                    const dateStr = usage.workDate.toString();
                    const cellLabel = `「${fertLabel}」の${uIndex + 1}番目の使用`;
                    const first = workRecordDateMapFert.get(usage.workRecordId);
                    if (!first) {
                        workRecordDateMapFert.set(usage.workRecordId, { date: dateStr, rowInfo: cellLabel });
                    } else if (first.date !== dateStr) {
                        fertChemErrorGroups.push(`${cellLabel}の日付が、同じ作業記録の他の行（${first.rowInfo}）と一致していません。同じ作業記録には同じ使用日を設定してください。`);
                    }
                }
                const usageQtyStr = usage.usageQty != null ? usage.usageQty.toString().trim() : '';
                const usageQtyNum = Number(usageQtyStr);
                const isUsageQtyFilled = usageQtyStr !== '';
                if (!isUsageQtyFilled) {
                    fertChemErrorGroups.push(`「${fertLabel}」の${uIndex + 1}番目の使用量を入力してください。`);
                } else if (!usageRegex.test(usageQtyStr) || usageQtyNum <= 0 || isNaN(usageQtyNum)) {
                    fertChemErrorGroups.push(`「${fertLabel}」の${uIndex + 1}番目の使用量は、0より大きい整数部7桁、小数部2桁までの数値を入力してください。`);
                }
                if (!usage.usageUnit) {
                    fertChemErrorGroups.push(`「${fertLabel}」の${uIndex + 1}番目の使用量の単位を入力してください。`);
                }
                const amountStr = usage.amount != null ? usage.amount.toString().trim() : '';
                if (amountStr !== '' && !amountRegex.test(amountStr)) {
                    fertChemErrorGroups.push(`「${fertLabel}」の${uIndex + 1}番目の使用の金額は10桁以内で入力してください。`);
                }
            });
        });
        const workRecordDateMapChem = new Map<string, { date: string; rowInfo: string }>();
        this.bulkChemicals.forEach((cvhc) => {
            const chemLabel = `${cvhc.chemicalName}${cvhc.warehouseName && cvhc.warehouseName !== '未管理' ? `（${cvhc.warehouseName}）` : ''}`;
            const seenDates = new Set<string>();
            cvhc.workUsages.forEach((usage, uIndex) => {
                if (cvhc.warehouseName !== '未管理' && !usage.workDate) {
                    fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の使用日を入力してください。`);
                }
                if (usage.workDate) {
                    const dateStr = usage.workDate.toString();
                    if (seenDates.has(dateStr)) {
                        fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の使用日は、同じ農薬で既に使用されています。別の日付を入力してください。`);
                    } else {
                        seenDates.add(dateStr);
                    }
                }
                if (usage.workRecordId && usage.workDate) {
                    const dateStr = usage.workDate.toString();
                    const cellLabel = `「${chemLabel}」の${uIndex + 1}番目の使用`;
                    const first = workRecordDateMapChem.get(usage.workRecordId);
                    if (!first) {
                        workRecordDateMapChem.set(usage.workRecordId, { date: dateStr, rowInfo: cellLabel });
                    } else if (first.date !== dateStr) {
                        fertChemErrorGroups.push(`${cellLabel}の日付が、同じ作業記録の他の行（${first.rowInfo}）と一致していません。同じ作業記録には同じ使用日を設定してください。`);
                    }
                }
                if (usage.disiDiv !== 1 && usage.disiDiv !== 2 && usage.disiDiv !== 3) {
                    fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の消毒を選択してください。`);
                }
                const usageQtyStr = usage.usageQty != null ? usage.usageQty.toString().trim() : '';
                const usageQtyNum = Number(usageQtyStr);
                const isUsageQtyFilled = usageQtyStr !== '';
                if (cvhc.warehouseName !== '未管理' && !isUsageQtyFilled) {
                    fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の使用量を入力してください。`);
                } else if (isUsageQtyFilled && (!usageRegex.test(usageQtyStr) || usageQtyNum <= 0 || isNaN(usageQtyNum))) {
                    fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の使用量は、0より大きい整数部7桁、小数部2桁までの数値を入力してください。`);
                }
                if (!usage.usageUnit) {
                    fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の使用量の単位を入力してください。`);
                }
                const amountStr = usage.amount != null ? usage.amount.toString().trim() : '';
                if (amountStr !== '' && !amountRegex.test(amountStr)) {
                    fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の使用の金額は10桁以内で入力してください。`);
                }
                if (this.sprayDilRatio) {
                    if (usage.dilRatioBranchNum != null) {
                        const dilRatioStr = usage.dilRatio != null ? usage.dilRatio.toString().trim() : '';
                        const dilRatioNum = Number(dilRatioStr);
                        const isDilRatioFilled = dilRatioStr !== '';
                        if (!isDilRatioFilled) {
                            fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の希釈倍数を入力してください。`);
                        } else if (!usageRegex.test(dilRatioStr) || dilRatioNum <= 0 || isNaN(dilRatioNum)) {
                            fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の希釈倍数は、0より大きい整数部7桁、小数部2桁までの数値を入力してください。`);
                        }
                        if (!usage.dilRatioUnit) {
                            fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の希釈倍数の単位を入力してください。`);
                        }
                    }
                    if (usage.sprayQtyBranchNum != null) {
                        const sprayQtyStr = usage.sprayQty != null ? usage.sprayQty.toString().trim() : '';
                        const sprayQtyNum = Number(sprayQtyStr);
                        const isSprayQtyFilled = sprayQtyStr !== '';
                        if (!isSprayQtyFilled) {
                            fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の散布量を入力してください。`);
                        } else if (!usageRegex.test(sprayQtyStr) || sprayQtyNum <= 0 || isNaN(sprayQtyNum)) {
                            fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の散布量は、0より大きい整数部7桁、小数部2桁までの数値を入力してください。`);
                        }
                        if (!usage.sprayUnit) {
                            fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の散布量の単位を入力してください。`);
                        }
                    }
                }
                const sharesStr = usage.shares != null ? usage.shares.toString().trim() : '';
                if (sharesStr !== '' && !amountRegex.test(sharesStr)) {
                    fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の株数は10桁以内で入力してください。`);
                }
            });
        });

        if (this.sprayDilRatio) {
            const distinctChemicalIds = Array.from(new Set(
                this.bulkChemicals.map(c => c.chemicalId).filter((id): id is number => id != null)
            ));
            if (distinctChemicalIds.length > 0) {
                this.isLoading = true;
                const settingsMap = await this.fetchChemicalConditionSettingsMap(distinctChemicalIds);
                this.isLoading = false;
                this.bulkChemicals.forEach((cvhc) => {
                    const settings = settingsMap.get(cvhc.chemicalId);
                    if (!settings || settings.caps.length === 0) return;
                    const chemLabel = `${cvhc.chemicalName}${cvhc.warehouseName && cvhc.warehouseName !== '未管理' ? `（${cvhc.warehouseName}）` : ''}`;
                    cvhc.workUsages.forEach((usage, uIndex) => {
                        if (!usage.appPartBranchNum) {
                            fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の適用部条件を選択してください。`);
                            return;
                        }
                        const matchedCap = settings.caps.find(c => c.branchNum === usage.appPartBranchNum);
                        if (!matchedCap) return;
                        if (matchedCap.usageConditions.length > 0 && !usage.usageCountBranchNum) {
                            fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の使用回数条件を選択してください。`);
                        }
                        if (matchedCap.dilutionConditions.length > 0 && !usage.dilRatioBranchNum) {
                            fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の希釈条件を選択してください。`);
                        }
                        if (matchedCap.sprayConditions.length > 0 && !usage.sprayQtyBranchNum) {
                            fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の散布条件を選択してください。`);
                        }
                        if ((matchedCap.ingConditions?.length || 0) > 0) {
                            const ingSelConditions = usage.ingSelConditions || [];
                            const hasMissing = matchedCap.ingConditions.some((ing) => {
                                if (!ing.conditions || ing.conditions.length === 0) return false;
                                const selected = ingSelConditions.find((sel) => sel.basicBranchNum === ing.branchNum);
                                if (!selected) return true;
                                const match = ing.conditions.some((detail) =>
                                    detail.branchNum === selected.ingUsageCntBranchNum &&
                                    detail.chemicalId === selected.chemicalId
                                );
                                return !match || selected.ingUsageCntBranchNum == null;
                            });
                            if (hasMissing) {
                                fertChemErrorGroups.push(`「${chemLabel}」の${uIndex + 1}番目の成分条件を選択してください。`);
                            }
                        }
                    });
                });
            }
        }

        const validationMessage = [basicTabMessage, workErrorGroups.join('\n\n'), fertChemErrorGroups.join('\n')].filter(Boolean).join('\n\n');
        if (validationMessage) {
            window.alert(validationMessage);
            return;
        }

        if (this.mode === "edit" && !window.confirm('内容を上書きして保存します。よろしいですか？')) return;

        const { sehiTableTotal, nouyakuTableTotal } = this;
        const fertTotal = this.sitem?.kcontTotal;
        const fertAlert = this.sitem?.kcontTotalAlert;
        const chemTotal = this.sitem?.chemicalUseCount;
        const chemAlert = this.sitem?.chemicalUseCountAlert;
        const limitMessages: string[] = [];
        if (this.selectedPairs.length < 2 && sehiTableTotal) {
            if (fertTotal != null && sehiTableTotal > fertTotal) {
                limitMessages.push('化学肥料（窒素）合計が上限値を超えました。');
            } else if (fertAlert != null && sehiTableTotal >= fertAlert) {
                limitMessages.push('化学肥料（窒素）合計が上限値に近づいています。');
            }
        }
        if (nouyakuTableTotal) {
            if (chemTotal != null && nouyakuTableTotal > chemTotal) {
                limitMessages.push('農薬使用点数合計が上限値を超えました。');
            } else if (chemAlert != null && nouyakuTableTotal >= chemAlert) {
                limitMessages.push('農薬使用点数合計が上限値に近づいています。');
            }
        }
        if (limitMessages.length && !confirm(limitMessages.join('\n') + '\nよろしいですか？')) return;

        this.isLoading = true;
        try {
            this.recomputeBulkProcessTypes();
            const pairsPayload = this.selectedPairs.map(pair => this.buildPairPayload(pair));
            const workRecordBatchesPayload = this.buildWorkRecordBatchesPayload();
            const fertilizerBatchesPayload = this.buildFertilizerBatchesPayload();
            const chemicalBatchesPayload = this.buildChemicalBatchesPayload();
            const expectedWorkRecordUpdates = this.buildExpectedWorkRecordUpdates();
            const response = await this.$apiClient.doRequest('post', `${this.baseUrl}/bulk`, { pairs: pairsPayload, workRecordBatches: workRecordBatchesPayload, fertilizerBatches: fertilizerBatchesPayload, chemicalBatches: chemicalBatchesPayload, expectedWorkRecordUpdates });
            if (response.message) {
                let alertText = response.message;
                if (response.lockFailedPlantingId && response.lockFailedAgriFieldId) {
                    const failedKey = this.pairKeyOf(response.lockFailedPlantingId, response.lockFailedAgriFieldId);
                    const failedPair = this.selectedPairs.find(pair => this.pairKeyOf(pair.plantingId, pair.agriFieldId) === failedKey);
                    if (failedPair) {
                        alertText = `「${this.pairListLabel(failedPair)}」: ${alertText}`;
                    }
                } else if (response.lockFailedWorkRecordBatchId) {
                    // lockFailedWorkRecordBatchId is a raw work_records.id (see buildExpectedWorkRecordUpdates),
                    // not a batch/usage id — match against workRecordId (and nested members/shares), not .id.
                    const failedBatch = this.bulkWorkRecords.find(b => (b.members ?? []).some(m => m.workRecordId === response.lockFailedWorkRecordBatchId));
                    const failedFertUsage = this.bulkFertilizers
                        .flatMap(f => f.workUsages.map(u => ({ ...u, fertilizerName: f.fertilizerName })))
                        .find(u => u.workRecordId === response.lockFailedWorkRecordBatchId || (u.shares ?? []).some(s => s.workRecordId === response.lockFailedWorkRecordBatchId));
                    const failedChemUsage = this.bulkChemicals
                        .flatMap(c => c.workUsages.map(u => ({ ...u, chemicalName: c.chemicalName })))
                        .find(u => u.workRecordId === response.lockFailedWorkRecordBatchId || (u.pairShares ?? []).some(s => s.workRecordId === response.lockFailedWorkRecordBatchId));
                    if (failedBatch && failedBatch.workItemName) {
                        alertText = `「${failedBatch.workItemName}」: ${alertText}`;
                    } else if (failedFertUsage) {
                        alertText = `「${failedFertUsage.fertilizerName}」: ${alertText}`;
                    } else if (failedChemUsage) {
                        alertText = `「${failedChemUsage.chemicalName}」: ${alertText}`;
                    }
                }
                window.alert(this.alertMessage + alertText);
                this.$toasted.error('変更に失敗しました。');
                return;
            }
            this.tempCultivationId = response.ids?.[0] ?? this.tempCultivationId;
            this.tempGroupId = response.groupIdsByCvId?.[response.ids?.[0]] ?? this.tempGroupId;

            this.$toasted.success('保存しました。');
            this.isCultivationHistorySaved = true;
            this.mode = "edit";
            this.$store.dispatch('doUpdateIsInputChanged', false);
            await new Promise(resolve => setTimeout(resolve, 400));
            await this.getCvHistoryGroup();
        } catch (error) {
            this.$toasted.error('保存に失敗しました。');
        } finally {
            this.isLoading = false;
        }
    }

    async getCvHistoryGroup() {
        this.isLoading = true;
        this.previousTenantId = null;
        this.previousComId = null;
        this.previousPdId = null;
        this.previousPdTenantId = null;
        this.previousSeedlingId = null;
        try {
            const rawRes = await this.$apiClient.doRequest('get', `${this.baseUrl}/groups/${this.tempGroupId}`);
            const res = toCamel(rawRes) as { records: any[]; workRecordBatches: any[]; fertilizerBatches: any[]; chemicalBatches: any[] };
            const records = (res.records ?? []).map(r => toCamel(r)) as any[];
            if (records.length === 0) {
                this.$toasted.error('データの取得に失敗しました。');
                return;
            }
            const first = records[0];

            this.form = this.getEmptyForm();
            this.form.tenantId = first.tenantId ?? null;
            this.form.comId = first.comId ?? null;
            this.form.pdId = first.pdId ?? null;
            this.form.pdTenantId = first.pdTenantId ?? null;
            this.form.sitemId = first.sitemId ?? null;
            this.sitem = first.shippingItem ? first.shippingItem as ShippingItemDetail : null;

            if (this.pesticidePlanProducer) {
                await this.getCollectionAndShippingGroups();
            } else if (this.isPesticidePlan) {
                await Promise.all([this.getCommittees(), this.getShippingItems()]);
            }
            await Promise.all([this.getSettingDetails(), this.getSeedlings(), this.getAgriFields()]);

            this.selectedPairs = records.map(r => this.pairFromDetail(r))
                .sort((a, b) => this.pairListLabel(a).localeCompare(this.pairListLabel(b), 'ja'));
            this.activePairIndex = 0;
            this.applyPairToForm(this.selectedPairs[0]);
            this.tempCultivationId = this.selectedPairs[0].existingCvId;
            this.editingOriginalPairKey = this.selectedPairs[0].plantingId && this.selectedPairs[0].agriFieldId
                ? `${this.selectedPairs[0].plantingId}_${this.selectedPairs[0].agriFieldId}`
                : null;

            this.previousTenantId = this.form.tenantId;
            this.previousComId = this.form.comId;
            this.previousPdId = this.form.pdId;
            this.previousPdTenantId = this.form.pdTenantId;
            this.previousSitemId = this.form.sitemId;
            this.previousSeedlingId = this.form.seedlingId;

            await Promise.all([this.getWarehouseChemicals(), this.getWarehouseFertilizers(), this.getWorkItems()]);

            const batches = (res.workRecordBatches ?? []).map(r => toCamel(r)) as any[];
            this.bulkWorkRecords = this.buildBulkWorkRecordsFromBatches(batches);
            this.deletedWorkRecordBatchIds = [];
            this.deletedWorkRecordExpectedUpdates.clear();

            const fertBatches = (res.fertilizerBatches ?? []).map(r => toCamel(r)) as any[];
            this.bulkFertilizers = this.groupFertilizerBatches(fertBatches);
            this.deletedFertilizerBatchIds = [];

            const chemBatches = (res.chemicalBatches ?? []).map(r => toCamel(r)) as any[];
            this.bulkChemicals = this.groupChemicalBatches(chemBatches);
            this.deletedChemicalBatchIds = [];
            this.snapshotBulkOriginals();
        } catch (error) {
            this.$toasted.error('データの取得に失敗しました。');
        } finally {
            this.isLoading = false;
        }
    }

    private formatPairName(plantingName: string | null, agriFieldName: string | null): string {
        if (plantingName && agriFieldName) return `${plantingName}（${agriFieldName}）`;
        return plantingName || agriFieldName || '';
    }

    pairListLabel(pair: PairBasicData): string {
        return this.formatPairName(pair.plantingName, pair.agriFieldName) || '未登録';
    }

    private effectivePairName(pair: PairBasicData, index: number): { plantingName: string | null; agriFieldName: string | null } {
        if (index === this.activePairIndex) {
            const agriFieldName = this.agriFields.find(af => af.id === this.form.agriFieldId)?.name ?? null;
            return { plantingName: this.form.plantingName, agriFieldName };
        }
        return { plantingName: pair.plantingName, agriFieldName: pair.agriFieldName };
    }

    pairDisplayLabel(pair: PairBasicData, index: number): string {
        const { plantingName, agriFieldName } = this.effectivePairName(pair, index);
        return this.formatPairName(plantingName, agriFieldName) || '未登録';
    }

    isPairNamePlaceholder(pair: PairBasicData, index: number): boolean {
        const { plantingName, agriFieldName } = this.effectivePairName(pair, index);
        return !plantingName && !agriFieldName;
    }

    private buildExpectedWorkRecordUpdates(): { id: string; updated: string }[] {
        const map = new Map<string, string>();
        const put = (id: string | null | undefined, updated: string | null | undefined) => {
            if (id && updated && !id.startsWith('temp::') && !map.has(id)) map.set(id, updated);
        };
        for (const wr of this.bulkWorkRecords) {
            for (const m of wr.members ?? []) put(m.workRecordId, m.updated);
        }
        for (const fert of this.bulkFertilizers) {
            for (const u of fert.workUsages) {
                put(u.workRecordId, u.updated);
                for (const s of u.shares ?? []) put(s.workRecordId, s.workRecordUpdated);
            }
        }
        for (const chem of this.bulkChemicals) {
            for (const u of chem.workUsages) {
                put(u.workRecordId, u.updated);
                for (const s of u.pairShares ?? []) put(s.workRecordId, s.workRecordUpdated);
            }
        }
        this.deletedWorkRecordExpectedUpdates.forEach((updated, id) => put(id, updated));
        return Array.from(map.entries()).map(([id, updated]) => ({ id, updated }));
    }

    private rememberDeletedUsageLock(usage: { workRecordId: string | null; updated: string | null } | null | undefined, shares?: ShareLock[] | null): void {
        if (!usage) return;
        if (usage.workRecordId && usage.updated) this.deletedWorkRecordExpectedUpdates.set(usage.workRecordId, usage.updated);
        for (const s of shares ?? []) {
            if (s.workRecordId && s.workRecordUpdated) this.deletedWorkRecordExpectedUpdates.set(s.workRecordId, s.workRecordUpdated);
        }
    }

    private rememberDeletedWorkRecordLock(batch: WorkRecordBatch): void {
        for (const m of batch.members ?? []) {
            if (m.workRecordId && m.updated) this.deletedWorkRecordExpectedUpdates.set(m.workRecordId, m.updated);
        }
    }

    private buildBulkWorkRecordsFromBatches(rows: any[]): WorkRecordBatch[] {
        return rows.map(r => ({
            id: r.id ?? null,
            workLinkId: r.id ?? null,
            workItemId: r.workItemId,
            workItemName: this.workItems.find(wi => wi.id === r.workItemId)?.name ?? null,
            workDate: r.workDate,
            editable: r.editable ?? true,
            hasProtectedResourceData: r.hasProtectedResourceData ?? false,
            processType: 'NOCHANGE' as const,
            members: r.members ?? [],
        }));
    }

    private groupFertilizerBatches(rows: any[]): CvhFertilizer[] {
        const groups = new Map<string, CvhFertilizer>();
        for (const r of rows) {
            const key = `${r.fertilizerId}::${r.warehouseId ?? 'UNMANAGED'}`;
            let group = groups.get(key);
            if (!group) {
                group = {
                    fertilizerId: r.fertilizerId,
                    resolvedFertilizerId: r.resolvedFertilizerId,
                    fertilizerName: r.fertilizerName,
                    maker: r.maker,
                    chemRetio: r.chemRetio,
                    warehouseId: r.warehouseId,
                    warehouseName: r.warehouseName ?? '未管理',
                    workUsages: [],
                    ownerClass: r.ownerClass ?? 1,
                };
                groups.set(key, group);
            }
            group.workUsages.push({
                localId: null,
                id: r.id ?? null,
                workRecordId: r.workLinkId ?? null,
                workDate: r.workDate,
                usageQty: r.usageQty,
                usageUnit: r.usageUnit,
                unitOfMeasure: r.unitOfMeasure,
                amount: r.amount,
                updated: r.updated ?? null,
                transactions: [],
                processType: 'NOCHANGE',
                editable: r.editable,
                shares: r.shares ?? null,
            });
        }
        return Array.from(groups.values());
    }

    get chemicalCropItemId(): string | null {
        return this.selectedPairs.length >= 2 ? (this.sitem?.cropItemId ?? null) : this.cropFamicId;
    }

    get bulkPairsForSplitModal(): { plantingId: string | null; tempPlantingKey: string | null; agriFieldId: string; label: string; area: number | null }[] {
        return this.selectedPairs
            .map((p, index) => ({ p, index }))
            .filter(({ p }) => (p.plantingId || p.tempPlantingKey) && p.agriFieldId)
            .map(({ p, index }) => ({ plantingId: p.plantingId, tempPlantingKey: p.tempPlantingKey, agriFieldId: p.agriFieldId as string, label: this.pairDisplayLabel(p, index), area: p.agriFieldUsageArea }));
    }

    selectFertTabPair(selection: string) {
        this.fertTabPairSelection = selection === 'bulk' ? 'bulk' : Number(selection);
    }

    get mergedIndividualFertilizers(): CvhFertilizer[] {
        const pair = this.fertTabPairSelection !== 'bulk' ? this.selectedPairs[this.fertTabPairSelection] : undefined;
        if (!pair) return [];

        const shareFor = (bulk: WorkUsage): PairShare | undefined =>
            (bulk.shares ?? undefined)?.find(s => this.sharePairKey(s.plantingId, s.tempPlantingKey, s.agriFieldId) === this.sharePairKey(pair.plantingId, pair.tempPlantingKey, pair.agriFieldId));

        const merged: CvhFertilizer[] = [];

        for (const bulkFert of this.bulkFertilizers) {
            const usages = bulkFert.workUsages
                .map(u => {
                    const share = shareFor(u);
                    if (!share) return null;
                    const useOriginal = u.processType === 'NOCHANGE' && share.originalUsageQty != null;
                    return {
                        ...u,
                        usageQty: useOriginal ? share.originalUsageQty : share.usageQty,
                        usageUnit: useOriginal ? (share.originalUsageUnit ?? u.usageUnit) : u.usageUnit,
                        unitOfMeasure: useOriginal ? (share.originalUnitOfMeasure ?? u.unitOfMeasure) : u.unitOfMeasure,
                        amount: share.amount,
                    };
                })
                .filter((u): u is WorkUsage => u !== null);
            if (usages.length === 0) continue;
            const keyId = bulkFert.resolvedFertilizerId ?? bulkFert.fertilizerId;
            let group = merged.find(f => (f.resolvedFertilizerId ?? f.fertilizerId) === keyId && f.warehouseId === bulkFert.warehouseId);
            if (!group) {
                group = { ...bulkFert, workUsages: [] };
                merged.push(group);
            }
            group.workUsages.push(...usages);
        }

        for (const f of pair.fertilizers) {
            const usages = f.workUsages.filter(u => u.fertLinkId == null);
            if (usages.length === 0) continue;
            let group = merged.find(g => (g.resolvedFertilizerId ?? g.fertilizerId) === f.fertilizerId && g.warehouseId === f.warehouseId);
            if (!group) {
                group = { ...f, workUsages: [] };
                merged.push(group);
            }
            group.workUsages.push(...usages);
        }
        return this.sortedFertilizers(merged);
    }

    get hasPendingBulkFertilizerChanges(): boolean {
        return this.bulkFertilizers.some(f => f.workUsages.some(u => u.processType !== 'NOCHANGE'))
            || this.deletedFertilizerBatchIds.length > 0;
    }

    formatBulkUsageQty(usage: WorkUsage, areaOverride?: number | null): string {
        const area = areaOverride !== undefined ? areaOverride : this.form.agriFieldUsageArea;
        if (!usage || usage.usageQty == null || !usage.usageUnit || !area) return '---';
        const { usageQty: q, usageUnit: u, unitOfMeasure: m } = usage;
        return this.isPer10aChecked
            ? `${this.formatNumberWithCommas(m === 1 ? q : (q / area * 10))} ${u}/10a`
            : `${this.formatNumberWithCommas(m === 1 ? (q * area / 10) : q)} ${u}${this.isPer10aChecked ? '/10a' : ''}`;
    }

    async openBulkFertilizerUsageModal(cvhf: CvhFertilizer, usage: WorkUsage | null, index: number | null) {
        await this.getWarehouseFertilizers();
        this.currentLocalId = Date.now().toString();
        this.isEditingBulkFertilizer = true;
        this.isReadOnly = false;

        const fert = this.bulkFertilizers.find(f => f.fertilizerId === cvhf.fertilizerId && f.warehouseId === cvhf.warehouseId);
        if (!fert) return;

        usage && (usage.localId = this.currentLocalId);

        const usedWorkDates = fert.workUsages
            .filter(u => u.localId !== usage?.localId && u.workDate)
            .map(u => u.workDate as Date);

        const units = this.filteredFertilizerUnits(cvhf.fertilizerId, cvhf.warehouseId);

        this.currentFertCell = {
            id: usage?.id ?? null,
            fertilizerId: cvhf.fertilizerId,
            fertilizerName: cvhf.fertilizerName,
            chemRetio: cvhf.chemRetio,
            warehouseId: cvhf.warehouseId,
            warehouseName: cvhf.warehouseName ?? "未管理",
            workRecordId: usage?.workRecordId ?? null,
            workDate: usage ? (usage.workDate ?? null) : this.today,
            usageQty: usage?.usageQty ?? null,
            usageUnit: usage?.usageUnit ?? (units.length > 0 ? units[0].name : "kg"),
            unitOfMeasure: usage?.unitOfMeasure ?? this.lastUsedUnitOfMeasure,
            usedDates: usedWorkDates,
            editable: usage?.editable ?? true,
            amount: usage?.amount ?? null,
            updated: usage?.updated ?? null,
            transactions: [],
            processType: usage?.processType ?? (usage?.id ? 'NOCHANGE' : 'ADD'),
            shares: usage?.shares ?? null,
        };

        this.showFertilizerUsageModal = true;
    }

    handleFertilizerUsageSave(usage: WorkUsage | null) {
        this.commitBulkFertilizerUsage(usage);
    }

    commitBulkFertilizerUsage(usage: WorkUsage | null) {
        const { fertilizerId, warehouseId } = this.currentFertCell || {};
        const fert = this.bulkFertilizers.find(f => f.fertilizerId === fertilizerId && f.warehouseId === warehouseId);
        if (!fert) return;

        const i = fert.workUsages.findIndex(w => w.localId === this.currentLocalId);
        const original = i !== -1 ? fert.workUsages[i] : null;

        if (usage) {
            usage.shares = this.preserveOriginalShareFields(usage.shares, original?.shares);
            this.lastUsedUnitOfMeasure = usage.unitOfMeasure as 0 | 1;
            if (!usage.id) {
                usage.id = `temp::${Date.now()}`;
                usage.processType = 'ADD';
            }
            usage.editable = true;

            let vacatedWorkRecordId: string | null = null;
            if (
                original?.workRecordId &&
                usage.workRecordId === original.workRecordId &&
                usage.workDate &&
                usage.workDate !== original.workDate
            ) {
                vacatedWorkRecordId = usage.workRecordId;
                usage.workRecordId = null;
                usage.updated = null;
            }
            if (usage.workRecordId === null && usage.workDate !== null) {
                usage.workRecordId = this.findOrCreateWorkRecordId('施肥', usage.workDate, (wrId) =>
                    this.bulkFertilizers.some(f =>
                        f.fertilizerId === fert.fertilizerId &&
                        f.warehouseId === fert.warehouseId &&
                        f.workUsages.some(u => u.workRecordId === wrId)
                    )
                );
            }

            if (i !== -1) fert.workUsages.splice(i, 1, usage);
            else fert.workUsages.push(usage);
            fert.workUsages.forEach(w => w.localId === this.currentLocalId && (w.localId = null));
            if (vacatedWorkRecordId) this.cleanupVacatedWorkRecord(vacatedWorkRecordId);
        } else {
            if (i !== -1) {
                const [removed] = fert.workUsages.splice(i, 1);
                if (removed?.id && !removed.id.startsWith('temp::')) this.deletedFertilizerBatchIds.push(removed.id);
                if (removed?.workRecordId) this.cleanupVacatedWorkRecord(removed.workRecordId);
            }
            this.bulkFertilizers = this.bulkFertilizers.filter(f => f !== fert || f.workUsages.length > 0);
        }
        this.currentLocalId = null;
        this.isEditingBulkFertilizer = false;
        this.recomputeBulkProcessTypes();
        this.inputChanged();
    }

    commitBulkFertilizers(value: Fertilizer[]) {
        this.inputChanged();
        const newFertilizers = value.filter(f =>
            !this.bulkFertilizers.some(ef => (ef.resolvedFertilizerId ?? ef.fertilizerId) === f.id && this.normalizeWarehouse(ef) === this.normalizeWarehouse(f))
        );
        const deletedFertilizers = this.bulkFertilizers.filter(f =>
            !value.some(v => v.id === (f.resolvedFertilizerId ?? f.fertilizerId) && this.normalizeWarehouse(v) === this.normalizeWarehouse(f))
        );
        this.bulkFertilizers = this.bulkFertilizers.filter(f =>
            value.some(v => v.id === (f.resolvedFertilizerId ?? f.fertilizerId) && this.normalizeWarehouse(v) === this.normalizeWarehouse(f))
        );
        deletedFertilizers.forEach(f => {
            (f.workUsages || []).forEach(wu => {
                if (wu.id && !wu.id.startsWith('temp::')) {
                    this.deletedFertilizerBatchIds.push(wu.id);
                    this.rememberDeletedUsageLock(wu, wu.shares);
                }
                if (wu.workRecordId) this.cleanupVacatedWorkRecord(wu.workRecordId);
            });
        });
        this.recomputeBulkProcessTypes();
        if (newFertilizers.length === 0) return;
        const newCvhFertilizers: CvhFertilizer[] = newFertilizers.map(f => ({
            fertilizerId: f.pdFertilizerId ?? f.id,
            resolvedFertilizerId: f.id,
            fertilizerName: f.name,
            maker: f.maker || null,
            chemRetio: f.chemRetio || null,
            warehouseId: f.warehouseId,
            warehouseName: f.warehouseName,
            workUsages: [],
            ownerClass: f.ownerClass ?? 1,
        }));
        this.bulkFertilizers.push(...newCvhFertilizers);
    }

    deleteBulkFertilizer(fertilizer: CvhFertilizer) {
        if (!window.confirm('削除しますか？')) return;
        this.inputChanged();
        const removed = this.bulkFertilizers.find(f => f.fertilizerId === fertilizer.fertilizerId && f.warehouseId === fertilizer.warehouseId);
        if (removed) {
            this.bulkFertilizers = this.bulkFertilizers.filter(f => f !== removed);
            (removed.workUsages || []).forEach(wu => {
                if (wu.id && !wu.id.startsWith('temp::')) {
                    this.deletedFertilizerBatchIds.push(wu.id);
                    this.rememberDeletedUsageLock(wu, wu.shares);
                }
                if (wu.workRecordId) this.cleanupVacatedWorkRecord(wu.workRecordId);
            });
            this.recomputeBulkProcessTypes();
        }
    }

    private buildFertilizerBatchesPayload() {
        const batches: any[] = [];
        for (const fert of this.bulkFertilizers) {
            for (const usage of fert.workUsages) {
                if (usage.processType !== 'ADD' && usage.processType !== 'UPDATE') continue;
                batches.push({
                    id: usage.processType === 'UPDATE' ? usage.id : undefined,
                    fertilizerId: fert.fertilizerId,
                    warehouseId: fert.warehouseId,
                    workDate: usage.workDate,
                    usageQty: usage.usageQty,
                    usageUnit: usage.usageUnit,
                    unitOfMeasure: usage.unitOfMeasure,
                    amount: usage.amount,
                    processType: usage.processType,
                    shares: usage.shares ?? undefined,
                    workLinkId: usage.workRecordId ?? undefined,
                });
            }
        }
        if (batches.length === 0 && this.deletedFertilizerBatchIds.length === 0) {
            return undefined;
        }
        return {
            batches,
            deletedBatchIds: this.deletedFertilizerBatchIds,
        };
    }

    private groupChemicalBatches(rows: any[]): CvhChemical[] {
        const groups = new Map<string, CvhChemical>();
        for (const r of rows) {
            const key = `${r.chemicalId}::${r.warehouseId ?? 'UNMANAGED'}::${r.disiDiv ?? ''}::${r.cropId ?? ''}::${r.appPartBranchNum ?? ''}::${r.usageCountBranchNum ?? ''}::${r.dilRatioBranchNum ?? ''}::${r.sprayQtyBranchNum ?? ''}::${this.buildIngKey(r.ingSelConditions ?? [])}`;
            let group = groups.get(key);
            if (!group) {
                group = {
                    chemicalId: r.chemicalId,
                    chemicalName: r.chemicalName,
                    warehouseId: r.warehouseId,
                    disiDiv: r.disiDiv,
                    warehouseName: r.warehouseName ?? '未管理',
                    memo: r.memo ?? null,
                    usageCountOrigin: r.usageCountOrigin ?? null,
                    usageCount: r.usageCount ?? null,
                    ingredients: r.ingredients ?? [],
                    cropId: r.cropId ?? null,
                    cropName: r.cropName ?? null,
                    appPartBranchNum: r.appPartBranchNum,
                    usageCountBranchNum: r.usageCountBranchNum,
                    dilRatioBranchNum: r.dilRatioBranchNum,
                    sprayQtyBranchNum: r.sprayQtyBranchNum,
                    dilRatioOrigin: r.dilRatioOrigin ?? null,
                    sprayQtyOrigin: r.sprayQtyOrigin ?? null,
                    al: r.al ?? null,
                    appPestWeed: r.appPestWeed ?? null,
                    purposeUse: r.purposeUse ?? null,
                    whenUse: r.whenUse ?? null,
                    howUse: r.howUse ?? null,
                    fumTime: r.fumTime ?? null,
                    fumTemp: r.fumTemp ?? null,
                    applSoil: r.applSoil ?? null,
                    applArea: r.applArea ?? null,
                    applChem: r.applChem ?? null,
                    workUsages: [],
                    isExpired: r.isExpired ?? false,
                    isNotSuitable: r.isNotSuitable ?? false,
                    hasCaps: r.hasCaps ?? false,
                    dilRatioMin: r.dilRatioMin ?? null,
                    dilRatioMax: r.dilRatioMax ?? null,
                    sprayQtyMin: r.sprayQtyMin ?? null,
                    sprayQtyMax: r.sprayQtyMax ?? null,
                    conditionsNotSelected: r.conditionsNotSelected ?? false,
                    cropOutOfScope: r.cropOutOfScope ?? false,
                    famicUpdated: r.famicUpdated ?? null,
                };
                groups.set(key, group);
            }
            group.workUsages.push({
                chemicalId: r.chemicalId,
                localId: null,
                id: r.id ?? null,
                workRecordId: r.workLinkId ?? null,
                workDate: r.workDate,
                cropId: null,
                appPartBranchNum: r.appPartBranchNum,
                usageCountBranchNum: r.usageCountBranchNum,
                dilRatioBranchNum: r.dilRatioBranchNum,
                sprayQtyBranchNum: r.sprayQtyBranchNum,
                unitOfMeasure: r.unitOfMeasure,
                dilRatio: r.dilRatio,
                dilRatioUnit: r.dilRatioUnit,
                disiDiv: r.disiDiv,
                usageQty: r.usageQty,
                usageUnit: r.usageUnit,
                sprayQty: r.sprayQty,
                sprayUnit: r.sprayUnit,
                shares: r.shares ?? null,
                ingSelConditions: r.ingSelConditions ?? [],
                amount: r.amount,
                updated: r.updated ?? null,
                transactions: [],
                processType: 'NOCHANGE',
                editable: r.editable,
                pairShares: r.pairShares ?? null,
                dilRatioAtSplit: r.dilRatio ?? null,
                sprayQtyAtSplit: r.sprayQty ?? null,
            });
        }
        return Array.from(groups.values());
    }

    selectChemTabPair(selection: string) {
        this.chemTabPairSelection = selection === 'bulk' ? 'bulk' : Number(selection);
    }

    get mergedIndividualChemicals(): CvhChemical[] {
        const pair = this.chemTabPairSelection !== 'bulk' ? this.selectedPairs[this.chemTabPairSelection] : undefined;
        if (!pair) return [];

        const shareFor = (bulk: ChemicalWorkUsage): ChemicalPairShare | undefined =>
            (bulk.pairShares ?? undefined)?.find(s => this.sharePairKey(s.plantingId, s.tempPlantingKey, s.agriFieldId) === this.sharePairKey(pair.plantingId, pair.tempPlantingKey, pair.agriFieldId));

        const chemGroupKey = (c: CvhChemical): string =>
            `${c.chemicalId}::${c.warehouseId ?? 'UNMANAGED'}::${c.disiDiv ?? ''}::${c.appPartBranchNum ?? ''}::${c.usageCountBranchNum ?? ''}::${c.dilRatioBranchNum ?? ''}::${c.sprayQtyBranchNum ?? ''}::${c.cropId ?? ''}::${this.buildChemIngKeyFromUsages(c.workUsages)}`;

        const mergedByKey = new Map<string, CvhChemical>();

        for (const bulkChem of this.bulkChemicals) {
            const usages = bulkChem.workUsages
                .map(u => {
                    const share = shareFor(u);
                    if (!share) return null;
                    const useOriginal = u.processType === 'NOCHANGE' && share.originalUsageQty != null;
                    return {
                        ...u,
                        usageQty: useOriginal ? share.originalUsageQty : share.usageQty,
                        usageUnit: useOriginal ? (share.originalUsageUnit ?? u.usageUnit) : u.usageUnit,
                        unitOfMeasure: useOriginal ? (share.originalUnitOfMeasure ?? u.unitOfMeasure) : u.unitOfMeasure,
                        amount: share.amount,
                    };
                })
                .filter((u): u is ChemicalWorkUsage => u !== null);
            if (usages.length === 0) continue;
            const key = chemGroupKey(bulkChem);
            let group = mergedByKey.get(key);
            if (!group) {
                group = { ...bulkChem, workUsages: [] };
                mergedByKey.set(key, group);
            }
            group.workUsages.push(...usages);
        }
        for (const c of pair.chemicals) {
            const usages = c.workUsages.filter(u => u.chemLinkId == null);
            if (usages.length === 0) continue;
            const key = chemGroupKey(c);
            let group = mergedByKey.get(key);
            if (!group) {
                group = { ...c, workUsages: [] };
                mergedByKey.set(key, group);
            }
            group.workUsages.push(...usages);
        }

        return this.sortedChemicals(Array.from(mergedByKey.values()));
    }

    get hasPendingBulkChemicalChanges(): boolean {
        return this.bulkChemicals.some(c => c.workUsages.some(u => u.processType !== 'NOCHANGE'))
            || this.deletedChemicalBatchIds.length > 0;
    }

    formatBulkChemicalUsageQty(usage: ChemicalWorkUsage, areaOverride?: number | null): string {
        const area = areaOverride !== undefined ? areaOverride : this.form.agriFieldUsageArea;
        if (!usage || usage.usageQty == null || !usage.usageUnit || !area) return '---';
        const { usageQty: q, usageUnit: u, unitOfMeasure: m } = usage;
        return this.isPer10aCheckedChemical
            ? `${this.formatNumberWithCommas(m === 1 ? q : (q / area * 10))} ${u}/10a`
            : `${this.formatNumberWithCommas(m === 1 ? (q * area / 10) : q)} ${u}${this.isPer10aCheckedChemical ? '/10a' : ''}`;
    }

    async openBulkPesticideUsageModal(cvhc: CvhChemical, usage: ChemicalWorkUsage | null, index: number | null) {
        await this.getWarehouseChemicals();
        this.currentLocalId = Date.now().toString();
        this.isEditingBulkChemical = true;
        this.isReadOnly = false;

        const cvhcIngKey = this.buildChemIngKeyFromUsages(cvhc.workUsages);
        const chem = this.bulkChemicals.find(c => c.chemicalId === cvhc.chemicalId && c.warehouseId === cvhc.warehouseId
            && c.disiDiv === cvhc.disiDiv && c.appPartBranchNum === cvhc.appPartBranchNum
            && c.usageCountBranchNum === cvhc.usageCountBranchNum && c.dilRatioBranchNum === cvhc.dilRatioBranchNum
            && c.sprayQtyBranchNum === cvhc.sprayQtyBranchNum && c.cropId === cvhc.cropId
            && this.buildChemIngKeyFromUsages(c.workUsages) === cvhcIngKey);
        if (!chem) return;

        usage && (usage.localId = this.currentLocalId);

        const usedWorkDates = chem.workUsages
            .filter(u => u.localId !== usage?.localId && u.workDate)
            .map(u => u.workDate as Date);

        const units = this.filteredChemicalUnits(cvhc.chemicalId, cvhc.warehouseId);
        const currentDisiDiv = usage?.disiDiv ?? cvhc.disiDiv;
        const normalizedDisiDiv = currentDisiDiv === 1 || currentDisiDiv === 2 || currentDisiDiv === 3 ? currentDisiDiv : 2;

        this.currentChemicalCell = {
            id: usage?.id ?? null,
            chemicalId: cvhc.chemicalId,
            chemicalName: cvhc.chemicalName,
            cropId: usage?.cropId ?? null,
            appPartBranchNum: usage?.appPartBranchNum ?? null,
            usageCountBranchNum: usage?.usageCountBranchNum ?? null,
            dilRatioBranchNum: usage?.dilRatioBranchNum ?? null,
            sprayQtyBranchNum: usage?.sprayQtyBranchNum ?? null,
            warehouseId: cvhc.warehouseId,
            warehouseName: cvhc.warehouseName ?? "未管理",
            workRecordId: usage?.workRecordId ?? null,
            workDate: usage ? (usage.workDate ?? null) : this.today,
            usageQty: usage?.usageQty ?? null,
            usageUnit: usage?.usageUnit ?? (units.length > 0 ? units[0].name : "kg"),
            sprayQty: usage?.sprayQty ?? null,
            sprayUnit: usage?.sprayUnit ?? "L",
            dilRatio: usage?.dilRatio ?? null,
            dilRatioUnit: usage?.dilRatioUnit ?? "倍",
            unitOfMeasure: usage?.unitOfMeasure ?? this.lastUsedUnitOfMeasure,
            disiDiv: normalizedDisiDiv,
            shares: usage?.shares ?? null,
            usedDates: usedWorkDates,
            editable: usage?.editable ?? true,
            isNotSuitable: cvhc.isNotSuitable ?? true,
            isExpired: cvhc.isExpired ?? false,
            ingSelConditions: usage?.ingSelConditions ?? [],
            amount: usage?.amount ?? null,
            hasCaps: cvhc.hasCaps ?? false,
            updated: usage?.updated ?? null,
            transactions: [],
            processType: usage?.processType ?? (usage?.id ? 'NOCHANGE' : 'ADD'),
            famicUpdated: chem.famicUpdated ?? null,
            cropOutOfScope: chem.cropOutOfScope ?? false,
            pairShares: usage?.pairShares ?? null,
            dilRatioAtSplit: usage?.dilRatioAtSplit ?? null,
            sprayQtyAtSplit: usage?.sprayQtyAtSplit ?? null,
        };

        this.showPesticideUsageModal = true;
    }

    handleChemicalUsageSave(usage: any | null) {
        this.commitBulkChemicalUsage(usage);
    }

    buildIngKey(ings: SelCondition[] = []): string {
        const filtered = (ings || []).filter(i => i.ingUsageCntBranchNum != null && i.usageCountOrigin != null);
        if (!filtered.length) return '';
        return filtered.map(i => `${i.chemicalId}-${i.basicBranchNum}-${i.usageCountOrigin}`).sort().join('|');
    }

    buildChemIngKeyFromUsages(workUsages?: ChemicalWorkUsage[] | null): string {
        const usages = workUsages || [];
        if (!usages.length) return '';
        const withKey = usages.find(u => this.buildIngKey(u.ingSelConditions || []) !== '');
        const target = withKey ?? usages[0];
        return this.buildIngKey(target.ingSelConditions || []);
    }

    commitBulkChemicalUsage(usage: any | null) {
        let removed: ChemicalWorkUsage | null = null;
        let sourceChem: CvhChemical | null = null;
        for (const c of this.bulkChemicals) {
            const idx = c.workUsages.findIndex(w => w.localId === this.currentLocalId);
            if (idx !== -1) {
                removed = c.workUsages.splice(idx, 1)[0] ?? null;
                sourceChem = c;
                break;
            }
        }
        this.bulkChemicals = this.bulkChemicals.filter(c => c !== sourceChem || c.workUsages.length > 0);

        if (!usage) {
            if (removed?.id && !removed.id.startsWith('temp::')) this.deletedChemicalBatchIds.push(removed.id);
            if (removed?.workRecordId) this.cleanupVacatedWorkRecord(removed.workRecordId);
            this.currentLocalId = null;
            this.isEditingBulkChemical = false;
            this.recomputeBulkProcessTypes();
            this.inputChanged();
            return;
        }

        usage.pairShares = this.preserveOriginalShareFields(usage.pairShares, removed?.pairShares);
        this.lastUsedUnitOfMeasure = usage.unitOfMeasure as 0 | 1;
        if (!usage.id) {
            usage.id = `temp::${Date.now()}`;
            usage.processType = 'ADD';
        }
        usage.editable = true;

        const eq = (a: any, b: any) => (a ?? null) === (b ?? null);

        let vacatedWorkRecordId: string | null = null;
        if (
            removed?.workRecordId &&
            usage.workRecordId === removed.workRecordId &&
            usage.workDate &&
            usage.workDate !== removed.workDate
        ) {
            vacatedWorkRecordId = usage.workRecordId;
            usage.workRecordId = null;
            usage.updated = null;
        }
        if (usage.workRecordId === null && usage.workDate !== null) {
            usage.workRecordId = this.findOrCreateWorkRecordId('農薬散布', usage.workDate, (wrId) =>
                this.bulkChemicals.some(c =>
                    c.chemicalId === usage.chemicalId &&
                    eq(c.warehouseId, usage.warehouseId) &&
                    c.workUsages.some(u => u.workRecordId === wrId)
                )
            );
        }
        const usageIngKey = this.buildIngKey(usage.ingSelConditions);
        let chem = this.bulkChemicals.find(c =>
            c.chemicalId === usage.chemicalId &&
            eq(c.warehouseId, usage.warehouseId) &&
            eq(c.disiDiv, usage.disiDiv) &&
            eq(c.appPartBranchNum, usage.appPartBranchNum) &&
            eq(c.usageCountBranchNum, usage.usageCountBranchNum) &&
            eq(c.dilRatioBranchNum, usage.dilRatioBranchNum) &&
            eq(c.sprayQtyBranchNum, usage.sprayQtyBranchNum) &&
            eq(c.cropId, usage.cropId) &&
            this.buildChemIngKeyFromUsages(c.workUsages) === usageIngKey
        );

        if (!chem) {
            const placeholder = this.bulkChemicals.find(c =>
                c.chemicalId === usage.chemicalId &&
                eq(c.warehouseId, usage.warehouseId) &&
                c.workUsages.length === 0 &&
                c.disiDiv == null && c.appPartBranchNum == null && c.usageCountBranchNum == null &&
                c.dilRatioBranchNum == null && c.sprayQtyBranchNum == null && c.cropId == null
            );

            const rowFields = {
                chemicalName: usage.chemicalName,
                warehouseName: usage.warehouseName ?? '未管理',
                disiDiv: usage.disiDiv,
                memo: usage.memo ?? null,
                usageCountOrigin: usage.usageCountOrigin ?? null,
                usageCount: usage.usageCount ?? null,
                ingredients: usage.ingredients ?? [],
                cropId: usage.cropId ?? null,
                cropName: usage.cropName ?? null,
                appPartBranchNum: usage.appPartBranchNum ?? null,
                usageCountBranchNum: usage.usageCountBranchNum ?? null,
                dilRatioBranchNum: usage.dilRatioBranchNum ?? null,
                sprayQtyBranchNum: usage.sprayQtyBranchNum ?? null,
                dilRatioOrigin: usage.dilRatioOrigin ?? null,
                sprayQtyOrigin: usage.sprayQtyOrigin ?? null,
                al: usage.al ?? null,
                appPestWeed: usage.appPestWeed ?? null,
                purposeUse: usage.purposeUse ?? null,
                whenUse: usage.whenUse ?? null,
                howUse: usage.howUse ?? null,
                fumTime: usage.fumTime ?? null,
                fumTemp: usage.fumTemp ?? null,
                applSoil: usage.applSoil ?? null,
                applArea: usage.applArea ?? null,
                applChem: usage.applChem ?? null,
                isExpired: usage.isExpired ?? false,
                isNotSuitable: usage.isNotSuitable ?? false,
                hasCaps: usage.hasCaps ?? false,
                dilRatioMin: usage.dilRatioMin ?? null,
                dilRatioMax: usage.dilRatioMax ?? null,
                sprayQtyMin: usage.sprayQtyMin ?? null,
                sprayQtyMax: usage.sprayQtyMax ?? null,
                conditionsNotSelected: usage.conditionsNotSelected ?? false,
                cropOutOfScope: usage.cropOutOfScope ?? false,
                famicUpdated: usage.famicUpdated ?? null,
            };

            if (placeholder) {
                Object.assign(placeholder, rowFields);
                chem = placeholder;
            } else {
                chem = {
                    chemicalId: usage.chemicalId,
                    warehouseId: usage.warehouseId ?? null,
                    workUsages: [],
                    ...rowFields,
                };
                this.bulkChemicals.push(chem);
            }
        }

        chem.workUsages.push({ ...usage, localId: null });
        if (vacatedWorkRecordId) this.cleanupVacatedWorkRecord(vacatedWorkRecordId);
        this.currentLocalId = null;
        this.isEditingBulkChemical = false;
        this.recomputeBulkProcessTypes();
        this.inputChanged();
    }

    async commitBulkChemicals(value: Chemical[]) {
        this.inputChanged();
        const newChemicals = value.filter(c =>
            !this.bulkChemicals.some(ec => ec.chemicalId === c.id && this.normalizeWarehouse(ec) === this.normalizeWarehouse(c))
        );
        const deletedChemicals = this.bulkChemicals.filter(c =>
            !value.some(v => v.id === c.chemicalId && this.normalizeWarehouse(v) === this.normalizeWarehouse(c))
        );
        this.bulkChemicals = this.bulkChemicals.filter(c =>
            value.some(v => v.id === c.chemicalId && this.normalizeWarehouse(v) === this.normalizeWarehouse(c))
        );
        deletedChemicals.forEach(c => {
            (c.workUsages || []).forEach(wu => {
                if (wu.id && !wu.id.startsWith('temp::')) {
                    this.deletedChemicalBatchIds.push(wu.id);
                    this.rememberDeletedUsageLock(wu, wu.pairShares);
                }
                if (wu.workRecordId) this.cleanupVacatedWorkRecord(wu.workRecordId);
            });
        });
        this.recomputeBulkProcessTypes();
        if (newChemicals.length === 0) return;
        let parsedChemicals: {
            chemicalId: number;
            memo: string;
            isExpired: boolean;
            isNotSuitable: boolean;
            hasCaps: boolean;
            ingredients: Ingredient[];
            famicUpdated: string | null;
        }[] = [];
        if (this.form.sitemId && this.cropFamicId && this.form.tenantId) {
            try {
                const res = await this.$apiClient.doRequest('post', `${this.baseUrl}/chemical_detail/${this.form.sitemId}/${this.cropFamicId}/${this.form.tenantId}`,
                    {
                        chemicalKeys: newChemicals.map(c => ({
                            chemicalId: c.id,
                        }))
                    }
                );
                parsedChemicals = (toCamel(res) as any).chemicals;
            } catch {
                this.$toasted.error('データの取得に失敗しました。');
            }
        }
        const newCvhChemicals: CvhChemical[] = newChemicals.map(c => {
            const match = parsedChemicals.find(pc => pc.chemicalId === c.id);
            return {
                chemicalId: c.id,
                chemicalName: c.name,
                warehouseId: c.warehouseId,
                disiDiv: null,
                warehouseName: c.warehouseName,
                memo: match?.memo ?? null,
                usageCountOrigin: null,
                usageCount: null,
                ingredients: match?.ingredients ?? [],
                cropId: null,
                cropName: null,
                appPartBranchNum: null,
                usageCountBranchNum: null,
                dilRatioBranchNum: null,
                sprayQtyBranchNum: null,
                dilRatioOrigin: null,
                sprayQtyOrigin: null,
                al: null,
                appPestWeed: null,
                purposeUse: null,
                whenUse: null,
                howUse: null,
                fumTime: null,
                fumTemp: null,
                applSoil: null,
                applArea: null,
                applChem: null,
                workUsages: [],
                isExpired: match?.isExpired ?? false,
                isNotSuitable: match?.isNotSuitable ?? false,
                hasCaps: match?.hasCaps ?? false,
                dilRatioMin: null,
                dilRatioMax: null,
                sprayQtyMin: null,
                sprayQtyMax: null,
                conditionsNotSelected: false,
                cropOutOfScope: false,
                famicUpdated: match?.famicUpdated ?? null,
            };
        });
        this.bulkChemicals.push(...newCvhChemicals);
    }

    deleteBulkChemical(chemical: CvhChemical) {
        if (!window.confirm('削除しますか？')) return;
        this.inputChanged();
        const chemicalIngKey = this.buildChemIngKeyFromUsages(chemical.workUsages);
        const removed = this.bulkChemicals.find(c =>
            c.chemicalId === chemical.chemicalId &&
            c.warehouseId === chemical.warehouseId &&
            c.disiDiv === chemical.disiDiv &&
            c.appPartBranchNum === chemical.appPartBranchNum &&
            c.usageCountBranchNum === chemical.usageCountBranchNum &&
            c.dilRatioBranchNum === chemical.dilRatioBranchNum &&
            c.sprayQtyBranchNum === chemical.sprayQtyBranchNum &&
            c.cropId === chemical.cropId &&
            this.buildChemIngKeyFromUsages(c.workUsages) === chemicalIngKey
        );
        if (removed) {
            this.bulkChemicals = this.bulkChemicals.filter(c => c !== removed);
            (removed.workUsages || []).forEach(wu => {
                if (wu.id && !wu.id.startsWith('temp::')) {
                    this.deletedChemicalBatchIds.push(wu.id);
                    this.rememberDeletedUsageLock(wu, wu.pairShares);
                }
                if (wu.workRecordId) this.cleanupVacatedWorkRecord(wu.workRecordId);
            });
            this.recomputeBulkProcessTypes();
        }
    }

    private buildChemicalBatchesPayload() {
        const batches: any[] = [];
        for (const chem of this.bulkChemicals) {
            for (const usage of chem.workUsages) {
                if (usage.processType !== 'ADD' && usage.processType !== 'UPDATE') continue;
                batches.push({
                    id: usage.processType === 'UPDATE' ? usage.id : undefined,
                    chemicalId: chem.chemicalId,
                    warehouseId: chem.warehouseId,
                    workDate: usage.workDate,
                    appPartBranchNum: usage.appPartBranchNum,
                    usageCountBranchNum: usage.usageCountBranchNum,
                    dilRatioBranchNum: usage.dilRatioBranchNum,
                    sprayQtyBranchNum: usage.sprayQtyBranchNum,
                    disiDiv: usage.disiDiv,
                    unitOfMeasure: usage.unitOfMeasure,
                    dilRatio: usage.dilRatio,
                    dilRatioUnit: usage.dilRatioUnit,
                    sprayQty: usage.sprayQty,
                    sprayUnit: usage.sprayUnit,
                    usageQty: usage.usageQty,
                    usageUnit: usage.usageUnit,
                    shares: usage.shares,
                    amount: usage.amount,
                    processType: usage.processType,
                    pairShares: usage.pairShares ?? undefined,
                    ingSelConditions: usage.ingSelConditions,
                    workLinkId: usage.workRecordId ?? undefined,
                });
            }
        }
        if (batches.length === 0 && this.deletedChemicalBatchIds.length === 0) {
            return undefined;
        }
        return {
            batches,
            deletedBatchIds: this.deletedChemicalBatchIds,
        };
    }

    formatIngredientUsageFor(chemicals: CvhChemical[]): Record<number, string> {
        const result: Record<number, string> = {};
        const sorted = this.sortedChemicals(chemicals);
        sorted.forEach((chem, index) => {
            const firstUsage = chem.workUsages?.[0];
            const conditions = firstUsage?.ingSelConditions || [];
            const lines = (chem.ingredients || []).map((ingredient: { name: string }) => {
                const match = conditions.find((c: { name: string }) => c.name === ingredient.name);
                return match?.usageCountOrigin || '---';
            });
            result[index] = lines.length ? lines.join('<br>') : '---';
        });
        return result;
    }

    get bulkFormatIngredientUsage(): Record<number, string> {
        return this.formatIngredientUsageFor(this.bulkChemicals);
    }

    get individualFormatIngredientUsage(): Record<number, string> {
        return this.formatIngredientUsageFor(this.mergedIndividualChemicals);
    }

    formatIngredientPointFor(cvhc: CvhChemical): number {
        return (cvhc.ingredients ?? []).reduce((s, i) => s + (i.pointValue ?? 0), 0);
    }

    get bulkTotalAgriFieldUsageArea(): number | null {
        const total = this.selectedPairs.reduce((sum, p) => sum + (p.agriFieldUsageArea ?? 0), 0);
        return total > 0 ? total : null;
    }

    openReadOnlyFertilizerUsageModal(cvhf: CvhFertilizer, usage: WorkUsage | null) {
        this.isReadOnly = true;
        this.isEditingBulkFertilizer = false;
        this.currentFertCell = {
            id: usage?.id ?? null,
            fertilizerId: cvhf.fertilizerId,
            fertilizerName: cvhf.fertilizerName,
            chemRetio: cvhf.chemRetio,
            warehouseId: cvhf.warehouseId,
            warehouseName: cvhf.warehouseName ?? "未管理",
            workRecordId: null,
            workDate: usage?.workDate ?? null,
            usageQty: usage?.usageQty ?? null,
            usageUnit: usage?.usageUnit ?? null,
            unitOfMeasure: usage?.unitOfMeasure ?? 0,
            usedDates: [],
            editable: false,
            amount: usage?.amount ?? null,
            updated: usage?.updated ?? null,
            transactions: [],
            processType: usage?.processType ?? 'NOCHANGE',
            shares: usage?.shares ?? null,
        };
        this.showFertilizerUsageModal = true;
    }

    openReadOnlyPesticideUsageModal(cvhc: CvhChemical, usage: ChemicalWorkUsage | null) {
        this.isReadOnly = true;
        this.isEditingBulkChemical = false;
        this.currentChemicalCell = {
            id: usage?.id ?? null,
            chemicalId: cvhc.chemicalId,
            chemicalName: cvhc.chemicalName,
            cropId: cvhc.cropId,
            appPartBranchNum: usage?.appPartBranchNum ?? cvhc.appPartBranchNum,
            usageCountBranchNum: usage?.usageCountBranchNum ?? cvhc.usageCountBranchNum,
            dilRatioBranchNum: usage?.dilRatioBranchNum ?? cvhc.dilRatioBranchNum,
            sprayQtyBranchNum: usage?.sprayQtyBranchNum ?? cvhc.sprayQtyBranchNum,
            warehouseId: cvhc.warehouseId,
            warehouseName: cvhc.warehouseName ?? "未管理",
            workRecordId: null,
            workDate: usage?.workDate ?? null,
            usageQty: usage?.usageQty ?? null,
            usageUnit: usage?.usageUnit ?? null,
            sprayQty: usage?.sprayQty ?? null,
            sprayUnit: usage?.sprayUnit ?? null,
            dilRatio: usage?.dilRatio ?? null,
            dilRatioUnit: usage?.dilRatioUnit ?? null,
            unitOfMeasure: usage?.unitOfMeasure ?? 0,
            disiDiv: usage?.disiDiv ?? cvhc.disiDiv ?? 2,
            shares: usage?.shares ?? null,
            usedDates: [],
            editable: false,
            isNotSuitable: cvhc.isNotSuitable,
            isExpired: cvhc.isExpired,
            ingSelConditions: usage?.ingSelConditions ?? [],
            amount: usage?.amount ?? null,
            hasCaps: cvhc.hasCaps,
            updated: usage?.updated ?? null,
            transactions: [],
            processType: usage?.processType ?? 'NOCHANGE',
            famicUpdated: cvhc.famicUpdated ?? null,
            cropOutOfScope: cvhc.cropOutOfScope ?? false,
            pairShares: usage?.pairShares ?? null,
        };
        this.showPesticideUsageModal = true;
    }

    private snapshotBulkOriginals(): void {
        this.originalBulkWorkRecords = this.bulkWorkRecords.map(b => ({ ...b }));
        this.originalBulkFertilizers = this.bulkFertilizers.map(f => ({
            ...f,
            workUsages: f.workUsages.map(u => ({ ...u, shares: u.shares ? u.shares.map(s => ({ ...s })) : u.shares })),
        }));
        this.originalBulkChemicals = this.bulkChemicals.map(c => ({
            ...c,
            workUsages: c.workUsages.map(u => ({
                ...u,
                ingSelConditions: (u.ingSelConditions || []).map(sc => ({ ...sc })),
                pairShares: u.pairShares ? u.pairShares.map(s => ({ ...s })) : u.pairShares,
            })),
        }));
    }

    private recomputeBulkProcessTypes(): void {
        const isSameValue = (a: unknown, b: unknown) => (a ?? null) === (b ?? null);
        const normalizeWorkDate = (workDate: Date | string | null): string | null => {
            if (!workDate) return null;
            if (workDate instanceof Date) {
                const year = workDate.getFullYear();
                const month = String(workDate.getMonth() + 1).padStart(2, '0');
                const day = String(workDate.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            }
            const value = String(workDate).trim();
            return value.includes('T') ? value.split('T')[0] : value;
        };
        const isSameFertShares = (a: PairShare[] | null | undefined, b: PairShare[] | null | undefined): boolean => {
            const listA = a ?? [];
            const listB = b ?? [];
            if (listA.length !== listB.length) return false;
            const keyOf = (s: PairShare) => this.sharePairKey(s.plantingId, s.tempPlantingKey, s.agriFieldId);
            const byKeyB = new Map(listB.map(s => [keyOf(s), s]));
            return listA.every(s => {
                const match = byKeyB.get(keyOf(s));
                return !!match && isSameValue(s.usageQty, match.usageQty) && isSameValue(s.amount, match.amount);
            });
        };
        const isSameChemShares = (a: ChemicalPairShare[] | null | undefined, b: ChemicalPairShare[] | null | undefined): boolean => {
            const listA = a ?? [];
            const listB = b ?? [];
            if (listA.length !== listB.length) return false;
            const keyOf = (s: ChemicalPairShare) => this.sharePairKey(s.plantingId, s.tempPlantingKey, s.agriFieldId);
            const byKeyB = new Map(listB.map(s => [keyOf(s), s]));
            return listA.every(s => {
                const match = byKeyB.get(keyOf(s));
                return !!match
                    && isSameValue(s.usageQty, match.usageQty)
                    && isSameValue(s.amount, match.amount);
            });
        };
        const isSameSelConditions = (a: SelCondition[], b: SelCondition[]): boolean => {
            if ((a || []).length !== (b || []).length) return false;
            return (a || []).every((cond, index) => {
                const target = (b || [])[index];
                if (!target) return false;
                return cond.basicBranchNum === target.basicBranchNum
                    && isSameValue(cond.ingUsageCntBranchNum, target.ingUsageCntBranchNum);
            });
        };

        const originalWorkRecordsById = new Map(this.originalBulkWorkRecords.map(b => [b.id, b]));
        this.bulkWorkRecords.forEach(batch => {
            if (batch.processType !== 'NOCHANGE' && batch.processType !== 'UPDATE') return;
            if (!batch.id) return;
            const original = originalWorkRecordsById.get(batch.id);
            if (!original) return;
            const currentWorkItem = batch.workItemId ?? batch.workItemName ?? null;
            const originalWorkItem = original.workItemId ?? original.workItemName ?? null;
            const isChanged = currentWorkItem !== originalWorkItem || normalizeWorkDate(batch.workDate) !== normalizeWorkDate(original.workDate);
            batch.processType = isChanged ? 'UPDATE' : 'NOCHANGE';
        });

        const affectedWorkRecordIds = new Set<string>();
        const markWorkRecordAffected = (workRecordId: string | null | undefined) => {
            if (workRecordId && originalWorkRecordsById.has(workRecordId)) affectedWorkRecordIds.add(workRecordId);
        };

        const originalFertUsagesById = new Map<string, WorkUsage>();
        this.originalBulkFertilizers.forEach(f => f.workUsages.forEach(u => { if (u.id) originalFertUsagesById.set(u.id, u); }));
        this.bulkFertilizers.forEach(fert => {
            fert.workUsages.forEach(usage => {
                if (usage.processType === 'ADD') {
                    markWorkRecordAffected(usage.workRecordId);
                    return;
                }
                if (usage.processType !== 'NOCHANGE' && usage.processType !== 'UPDATE') return;
                if (!usage.id || usage.id.startsWith('temp::')) return;
                const original = originalFertUsagesById.get(usage.id);
                if (!original) return;
                const isChanged = normalizeWorkDate(usage.workDate) !== normalizeWorkDate(original.workDate)
                    || !isSameValue(usage.usageQty, original.usageQty)
                    || !isSameValue(usage.usageUnit, original.usageUnit)
                    || !isSameValue(usage.unitOfMeasure, original.unitOfMeasure)
                    || !isSameValue(usage.amount, original.amount)
                    || !isSameFertShares(usage.shares, original.shares);
                if (isChanged) {
                    markWorkRecordAffected(original.workRecordId);
                    markWorkRecordAffected(usage.workRecordId);
                }
                usage.processType = isChanged ? 'UPDATE' : 'NOCHANGE';
            });
        });
        const currentFertUsageIds = new Set<string>();
        this.bulkFertilizers.forEach(f => f.workUsages.forEach(u => { if (u.id) currentFertUsageIds.add(u.id); }));
        this.originalBulkFertilizers.forEach(f => f.workUsages.forEach(u => {
            if (u.id && !currentFertUsageIds.has(u.id)) markWorkRecordAffected(u.workRecordId);
        }));

        const originalChemUsagesById = new Map<string, ChemicalWorkUsage>();
        this.originalBulkChemicals.forEach(c => c.workUsages.forEach(u => { if (u.id) originalChemUsagesById.set(u.id, u); }));
        this.bulkChemicals.forEach(chem => {
            chem.workUsages.forEach(usage => {
                if (usage.processType === 'ADD') {
                    markWorkRecordAffected(usage.workRecordId);
                    return;
                }
                if (usage.processType !== 'NOCHANGE' && usage.processType !== 'UPDATE') return;
                if (!usage.id || usage.id.startsWith('temp::')) return;
                const original = originalChemUsagesById.get(usage.id);
                if (!original) return;
                const isChanged = normalizeWorkDate(usage.workDate) !== normalizeWorkDate(original.workDate)
                    || !isSameValue(usage.appPartBranchNum, original.appPartBranchNum)
                    || !isSameValue(usage.usageCountBranchNum, original.usageCountBranchNum)
                    || !isSameValue(usage.dilRatioBranchNum, original.dilRatioBranchNum)
                    || !isSameValue(usage.sprayQtyBranchNum, original.sprayQtyBranchNum)
                    || !isSameValue(usage.unitOfMeasure, original.unitOfMeasure)
                    || !isSameValue(usage.dilRatio, original.dilRatio)
                    || !isSameValue(usage.dilRatioUnit, original.dilRatioUnit)
                    || !isSameValue(usage.disiDiv, original.disiDiv)
                    || !isSameValue(usage.usageQty, original.usageQty)
                    || !isSameValue(usage.usageUnit, original.usageUnit)
                    || !isSameValue(usage.sprayQty, original.sprayQty)
                    || !isSameValue(usage.sprayUnit, original.sprayUnit)
                    || !isSameValue(usage.amount, original.amount)
                    || !isSameSelConditions(usage.ingSelConditions || [], original.ingSelConditions || [])
                    || !isSameChemShares(usage.pairShares, original.pairShares);
                if (isChanged) {
                    markWorkRecordAffected(original.workRecordId);
                    markWorkRecordAffected(usage.workRecordId);
                }
                usage.processType = isChanged ? 'UPDATE' : 'NOCHANGE';
            });
        });
        const currentChemUsageIds = new Set<string>();
        this.bulkChemicals.forEach(c => c.workUsages.forEach(u => { if (u.id) currentChemUsageIds.add(u.id); }));
        this.originalBulkChemicals.forEach(c => c.workUsages.forEach(u => {
            if (u.id && !currentChemUsageIds.has(u.id)) markWorkRecordAffected(u.workRecordId);
        }));

        this.bulkWorkRecords.forEach(batch => {
            if (batch.processType !== 'NOCHANGE') return;
            if (batch.id && affectedWorkRecordIds.has(batch.id)) {
                batch.processType = 'UPDATE';
            }
        });
    }

    private async fetchChemicalConditionSettingsMap(chemicalIds: number[]): Promise<Map<number, ChemConditionSettings>> {
        const map = new Map<number, ChemConditionSettings>();
        const cropItemId = this.chemicalCropItemId;
        const tenantId = this.form.tenantId ? this.form.tenantId : null;
        await Promise.all(chemicalIds.map(async (chemicalId) => {
            try {
                const res = await this.$apiClient.doRequest('get', `/admin/chemicals/additional_settings/${chemicalId}/${cropItemId}/${tenantId}`);
                map.set(chemicalId, toCamel(res) as ChemConditionSettings);
            } catch {
                this.$toasted.error('データの取得に失敗しました。');
            }
        }));
        return map;
    }

    get isNewPlantingPeriodValid(): boolean {
        return !!this.form.plantingStartDate && !!this.form.plantingEndDate && this.form.plantingStartDate < this.form.plantingEndDate;
    }

    private sharePairKey(plantingId: string | null | undefined, tempPlantingKey: string | null | undefined, agriFieldId: string | null | undefined): string {
        return `${plantingId ?? tempPlantingKey ?? ''}_${agriFieldId ?? ''}`;
    }

    private findOrCreateWorkRecordId(workItemName: '施肥' | '農薬散布', workDate: Date | string, isAlreadyClaimed: (workRecordId: string) => boolean): string {
        const existing = this.bulkWorkRecords.find(wr =>
            wr.id && wr.workItemName === workItemName && wr.workDate === workDate
            && !wr.hasProtectedResourceData && !isAlreadyClaimed(wr.id)
        );
        if (existing) return existing.id;
        const wrId = `temp::${Date.now()}`;
        this.bulkWorkRecords.unshift({
            id: wrId,
            workLinkId: null,
            workItemId: null,
            workItemName,
            workDate,
            editable: true,
            hasProtectedResourceData: false,
            processType: 'ADD',
        });
        return wrId;
    }

    private workRecordKey(batch: { id: string | null }): string | null {
        return batch.id || null;
    }

    private propagateWorkRecordDate(workRecordId: string, workDate: Date | string): void {
        if (!workRecordId) return;
        this.bulkFertilizers.forEach(f => f.workUsages.forEach(w => { if (w.workRecordId === workRecordId) w.workDate = workDate; }));
        this.bulkChemicals.forEach(c => c.workUsages.forEach(w => { if (w.workRecordId === workRecordId) w.workDate = workDate; }));
        this.bulkWorkRecords.forEach(wr => { if (this.workRecordKey(wr) === workRecordId) wr.workDate = workDate; });
    }

    private cleanupVacatedWorkRecord(workRecordId: string): void {
        if (!workRecordId || this.isWorkRecordLinkedToFertOrChem(workRecordId)) return;
        if (workRecordId.startsWith('temp::')) {
            this.bulkWorkRecords = this.bulkWorkRecords.filter(wr => wr.id !== workRecordId);
            return;
        }
        const batch = this.bulkWorkRecords.find(wr => wr.id === workRecordId);
        if (!batch || batch.hasProtectedResourceData) return;
        this.bulkWorkRecords = this.bulkWorkRecords.filter(wr => wr.id !== workRecordId);
        this.deletedWorkRecordBatchIds.push(workRecordId);
        this.rememberDeletedWorkRecordLock(batch);
    }

    private compareDatesDescending(a: Date | string | null, b: Date | string | null): number {
        if ((a == null || a === '') && (b == null || b === '')) return 0;
        if (a == null || a === '') return 1;
        if (b == null || b === '') return -1;
        return new Date(b).getTime() - new Date(a).getTime();
    }

    private isWorkRecordLinkedToFertOrChem(workRecordId: string | null): boolean {
        if (!workRecordId) return false;
        return this.bulkFertilizers.some(f => f.workUsages.some(u => u.workRecordId === workRecordId))
            || this.bulkChemicals.some(c => c.workUsages.some(u => u.workRecordId === workRecordId));
    }

    isWorkRecordBatchEditable(batch: WorkRecordBatch): boolean {
        return batch.editable && !this.isWorkRecordLinkedToFertOrChem(batch.id);
    }

    private preserveOriginalShareFields<T extends { plantingId: string | null; tempPlantingKey?: string | null; agriFieldId: string; originalUsageQty?: number | null; originalUsageUnit?: string | null; originalUnitOfMeasure?: number | null }>(
        newShares: T[] | null | undefined, oldShares: T[] | null | undefined
    ): T[] | null | undefined {
        if (!newShares || !oldShares) return newShares;
        const oldByKey = new Map(oldShares.map(s => [this.sharePairKey(s.plantingId, s.tempPlantingKey, s.agriFieldId), s]));
        return newShares.map(s => {
            const match = oldByKey.get(this.sharePairKey(s.plantingId, s.tempPlantingKey, s.agriFieldId));
            return match ? { ...s, originalUsageQty: match.originalUsageQty, originalUsageUnit: match.originalUsageUnit, originalUnitOfMeasure: match.originalUnitOfMeasure } : s;
        });
    }

    get isModalBusy(): boolean {
        return this.isLoading || this.isInitializing;
    }
}
